import React, { useRef, useCallback, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_BASE = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';

// Megatank gerçekleşen rapor — canlı çekilen Meta kampanyalarını isimden sınıflandırır.
// Türkçe diakritikleri sadeleştir (İ/ç/ö/ş/ğ güvenli eşleştirme)
const mtNorm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
// Kampanya bir Megatank/Değerli Kağıt kampanyası mı?
const mtIsMatch = (name) => /valuablepaper|megatank|degerli|rival/.test(mtNorm(name));
// İsimden konsept / format / kitle çıkar
const mtParse = (name) => {
  const n = mtNorm(name);
  const concept = /rival/.test(n) ? 'Rival' : (/valuablepaper|degerli/.test(n) ? 'Valuable Paper' : 'Megatank');
  const format = /video/.test(n) ? 'Video' : (/static|statik/.test(n) ? 'Statik' : '—');
  const audience = /office/.test(n) ? 'Office' : (/home/.test(n) ? 'Home' : 'Karma');
  return { concept, format, audience };
};

// ── Megatank değerlendirme metni — canlı veriden türetilen yazılı yorum ──────
// Kural tabanlı (harici servis yok): format/konsept/kreatif kırılımını kıyaslar,
// medya-satın-alma dilinde nedeniyle birlikte anlatır. Rapora "insight" katmanı.
const _mtI = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const _mtC = (n) => '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR');
const _mt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const _mtAgg = (list) => {
  const spend = list.reduce((s, c) => s + Number(c.spend || 0), 0);
  const impr = list.reduce((s, c) => s + Number(c.impressions || 0), 0);
  const reach = list.reduce((s, c) => s + Number(c.reach || 0), 0);
  const clicks = list.reduce((s, c) => s + Number(c.clicks || 0), 0);
  return {
    spend, impr, reach, clicks,
    ctr: impr > 0 ? (clicks / impr) * 100 : 0,
    cpm: impr > 0 ? (spend / impr) * 1000 : 0,
    cpc: clicks > 0 ? spend / clicks : 0,
    freq: reach > 0 ? impr / reach : 0,
  };
};

function mtBuildInsights(camps) {
  if (!Array.isArray(camps) || camps.length === 0) return [];
  const A = _mtAgg(camps);
  const rel = (a, b) => (a > 0 && b > 0) ? (Math.max(a, b) - Math.min(a, b)) / Math.min(a, b) : 1;
  const out = [];

  out.push(
    `Dönem boyunca ${_mtC(A.spend)} medya bütçesiyle ${_mtI(A.impr)} gösterim alındı ve yaklaşık ${_mtI(A.reach)} kişiye ulaşıldı. ` +
    `Ortalama frekans ${_mt2(A.freq)}, tıklama oranı %${_mt2(A.ctr)}, bin gösterim maliyeti (CPM) ₺${_mt2(A.cpm)} seviyesinde gerçekleşti. ` +
    `Tıklama oranının bu seviyede kalması bilinirlik hedefli kurguda beklenen bir tablo; kıyas büyük ölçüde erişim ve gösterim verimi üzerinden yapıldı.`
  );

  const engagement = camps.reduce((t, c) => t + Number(c.engagement || 0), 0);
  const engRate = A.impr > 0 ? engagement / A.impr : 0;
  if (engagement > 0) {
    out.push(`Kurgunun asıl kazanımı etkileşim tarafında oldu: toplam ${_mtI(engagement)} etkileşim ve %${_mt2(engRate * 100)} etkileşim oranı, içeriğin gösterim başına güçlü bir ilgi ürettiğini ve marka hatırlanırlığına doğrudan katkı sağladığını gösteriyor.`);
  }
  if (A.cpm > 0) {
    out.push(`Erişim verimi tarafında ${_mtI(A.reach)} tekil kişiye, bin gösterim başına ₺${_mt2(A.cpm)} CPM ile ulaşıldı; bilinirlik hedefinde bu maliyet seviyesi, geniş kitleye görece uygun bir maliyetle temas edildiği anlamına geliyor.`);
  }

  const vids = camps.filter((c) => c.format === 'Video');
  const stat = camps.filter((c) => c.format === 'Statik');
  if (vids.length && stat.length) {
    const V = _mtAgg(vids), S = _mtAgg(stat);
    const clauses = [];
    let clear = false;
    if (V.cpc > 0 && S.cpc > 0) {
      const g = rel(V.cpc, S.cpc);
      if (g < 0.06) {
        clauses.push(`tıklama maliyeti iki tarafta da benzer seyretti (video ₺${_mt2(V.cpc)} · statik ₺${_mt2(S.cpc)})`);
      } else {
        if (g >= 0.15) clear = true;
        const cheaper = V.cpc < S.cpc ? 'video' : 'statik';
        clauses.push(`trafiği ${cheaper} kreatifler ${_mt2(Math.max(V.cpc, S.cpc) / Math.min(V.cpc, S.cpc))} kat daha uygun maliyetle getirdi (video ₺${_mt2(V.cpc)} · statik ₺${_mt2(S.cpc)})`);
      }
    }
    if (V.cpm > 0 && S.cpm > 0) {
      const g = rel(V.cpm, S.cpm);
      if (g < 0.06) {
        clauses.push(`CPM tarafında fark ihmal edilebilir düzeyde, iki format pratikte başabaş (video ₺${_mt2(V.cpm)} · statik ₺${_mt2(S.cpm)})`);
      } else {
        if (g >= 0.15) clear = true;
        const better = V.cpm < S.cpm ? 'video' : 'statik';
        clauses.push(`gösterim maliyetinde ${better} ${g < 0.15 ? 'bir miktar' : 'belirgin şekilde'} daha uygun (video ₺${_mt2(V.cpm)} · statik ₺${_mt2(S.cpm)})`);
      }
    }
    if (clauses.length) {
      const lead = clear ? 'Format tarafında video ve statik kreatifler ayrıştı: ' : 'Video ve statik kreatifler genel olarak birbirine yakın kaldı; ';
      out.push(lead + clauses.join(', ') + '.');
    }
  }

  const riv = camps.filter((c) => c.concept === 'Rival');
  const vp = camps.filter((c) => c.concept === 'Valuable Paper');
  if (riv.length && vp.length) {
    const R = _mtAgg(riv), P = _mtAgg(vp);
    const bits = [];
    if (R.cpm > 0 && P.cpm > 0) {
      const g = rel(R.cpm, P.cpm);
      if (g < 0.06) bits.push(`gösterim maliyetleri neredeyse eşit (Rival ₺${_mt2(R.cpm)} · Valuable Paper ₺${_mt2(P.cpm)})`);
      else { const eff = R.cpm < P.cpm ? 'Rival' : 'Valuable Paper'; bits.push(`gösterim başına maliyette ${eff} ${g < 0.15 ? 'hafif' : 'belirgin'} önde (Rival ₺${_mt2(R.cpm)} · Valuable Paper ₺${_mt2(P.cpm)})`); }
    }
    if (R.ctr > 0 && P.ctr > 0) {
      const g = rel(R.ctr, P.ctr);
      if (g < 0.06) bits.push(`tıklama oranları da yakın (%${_mt2(R.ctr)} · %${_mt2(P.ctr)})`);
      else bits.push(`tıklama oranında ${R.ctr > P.ctr ? 'Rival' : 'Valuable Paper'} önde (%${_mt2(R.ctr)} · %${_mt2(P.ctr)})`);
    }
    out.push(`Konsept bazında ${_mtI(R.impr)} gösterimlik Rival ve ${_mtI(P.impr)} gösterimlik Valuable Paper birbirine yakın hacimde ilerledi` + (bits.length ? `; ${bits.join(', ')}.` : '.'));
  }

  const share = (list) => A.spend > 0 ? (list.reduce((t, c) => t + Number(c.spend || 0), 0) / A.spend) * 100 : 0;
  const rivalC = camps.filter((c) => c.concept === 'Rival');
  const vpC = camps.filter((c) => c.concept === 'Valuable Paper');
  if (rivalC.length && vpC.length && A.spend > 0) {
    out.push(`Bütçe dağılımında harcamanın %${_mt2(share(rivalC))}'i Rival, %${_mt2(share(vpC))}'i Valuable Paper kurgusuna ayrıldı; iki konsept de bilinirlik kırılımında dengeli bir ağırlıkla yürütüldü.`);
  }

  const wr = camps.map((c) => {
    const impr = Number(c.impressions || 0), clicks = Number(c.clicks || 0), spend = Number(c.spend || 0), reach = Number(c.reach || 0);
    return { ...c, _ctr: impr > 0 ? (clicks / impr) * 100 : 0, _cpm: impr > 0 ? (spend / impr) * 1000 : 0, _freq: reach > 0 ? impr / reach : 0 };
  });
  const best = [...wr].filter((c) => c._ctr > 0).sort((a, b) => b._ctr - a._ctr)[0];
  const pricey = [...wr].filter((c) => c._cpm > 0).sort((a, b) => b._cpm - a._cpm)[0];
  if (best) {
    let s = `Kreatif kırılımında en yüksek ilgiyi "${best.campaign_name}" topladı (%${_mt2(best._ctr)} tıklama oranı).`;
    if (pricey && pricey.campaign_id !== best.campaign_id && A.cpm > 0 && pricey._cpm > A.cpm * 1.2) {
      s += ` "${pricey.campaign_name}" ise ₺${_mt2(pricey._cpm)} CPM ile ortalamanın (₺${_mt2(A.cpm)}) belirgin üzerinde kaldı; bu kırılımda hedefleme daraltması ya da kreatif tazelemesi denenebilir.`;
    }
    out.push(s);
  }

  const hf = wr.filter((c) => c._freq >= 2.4).sort((a, b) => b._freq - a._freq)[0];
  if (hf) {
    out.push(
      `Sıklık tarafında "${hf.campaign_name}" ${_mt2(hf._freq)} frekansla erişim doygunluğuna yaklaştı. ` +
      `Aynı kitleye tekrar gösterim arttığından, kalan bütçeyi taze/benzer kitleye kaydırmak erişim verimini korumak için yerinde olur.`
    );
  }

  out.push(
    `Genel tabloda kampanya, awareness hedefine uygun biçimde yüksek erişim ve güçlü etkileşim üretti. ` +
    `Önümüzdeki dönemde en verimli çalışan format ve kurguya ağırlık vererek, aynı bütçeyle daha fazla nitelikli erişim ve etkileşim hedeflenebilir.`
  );

  return out;
}


export default function CanonApril2026() {
  const slide1Ref = useRef(null);
  const slide2Ref = useRef(null);
  const slide3Ref = useRef(null);
  const wsSlide1Ref = useRef(null);
  const wsSlide2Ref = useRef(null);
  const wsSlide3Ref = useRef(null);
  const mtSlide1Ref = useRef(null);
  const mtSlide2Ref = useRef(null);
  const mtSlide3Ref = useRef(null);
  const mtRevSlideRef = useRef(null);
  const ifsakSlideRef = useRef(null);
  const mtReportRef = useRef(null);
  const mtMediaRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // ─── Megatank GERÇEKLEŞEN rapor — canlı Meta verisi ───
  const [mtRange, setMtRange] = useState('all');
  const [mtRep, setMtRep] = useState(null);
  const [mtLoading, setMtLoading] = useState(false);
  const [mtErr, setMtErr] = useState(null);

  const loadMtReport = useCallback(async () => {
    setMtLoading(true); setMtErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/meta-ads/live/campaign-insights?dateRange=${encodeURIComponent(mtRange)}`, {
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        credentials: 'include',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `İstek başarısız (${res.status})`);
      }
      const data = await res.json();
      setMtRep(data);
    } catch (e) {
      setMtErr(e.message || 'Canlı veri çekilemedi');
    } finally {
      setMtLoading(false);
    }
  }, [mtRange]);

  useEffect(() => { loadMtReport(); }, [loadMtReport]);

  // ─── Megatank medya + hedef kitle — canlı çekim (rapor yüklenince) ───
  const [mtMedia, setMtMedia] = useState(null);
  const [mtMediaLoading, setMtMediaLoading] = useState(false);
  const [mtMediaErr, setMtMediaErr] = useState(null);

  useEffect(() => {
    const ids = (mtRep?.campaigns || [])
      .filter((c) => mtIsMatch(c.campaign_name))
      .map((c) => c.campaign_id)
      .filter(Boolean);
    if (!ids.length) { setMtMedia(null); return; }
    let cancelled = false;
    (async () => {
      setMtMediaLoading(true); setMtMediaErr(null);
      try {
        const res = await fetch(
          `${API_BASE}/api/meta-ads/live/campaign-media-audience?campaignIds=${encodeURIComponent(ids.join(','))}`,
          { headers: { 'X-Brand-ID': CANON_BRAND_ID }, credentials: 'include' }
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || `İstek başarısız (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) setMtMedia(data);
      } catch (e) {
        if (!cancelled) setMtMediaErr(e.message || 'Medya/kitle çekilemedi');
      } finally {
        if (!cancelled) setMtMediaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mtRep]);

  const handleExportPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const refs = [slide1Ref, slide2Ref, slide3Ref];
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      for (let i = 0; i < refs.length; i++) {
        const el = refs[i].current;
        if (!el) continue;
        if (i > 0) pdf.addPage();

        const canvas = await html2canvas(el, {
          scale: 3,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        const x = margin + (usableW - w) / 2;
        const y = margin;

        pdf.addImage(imgData, 'JPEG', x, y, w, h);
      }

      pdf.save('Canon_Nisan_2026_Medya_Plani.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const handleExportWorkshopPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const refs = [wsSlide1Ref, wsSlide2Ref, wsSlide3Ref];
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      for (let i = 0; i < refs.length; i++) {
        const el = refs[i].current;
        if (!el) continue;
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(el, {
          scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        const x = margin + (usableW - w) / 2;
        const y = margin;
        pdf.addImage(imgData, 'JPEG', x, y, w, h);
      }
      pdf.save('Canon_Workshop_Kayit_Kampanyasi.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const handleExportMegatankPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const refs = [mtSlide1Ref, mtSlide2Ref, mtSlide3Ref];
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      for (let i = 0; i < refs.length; i++) {
        const el = refs[i].current;
        if (!el) continue;
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(el, {
          scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        const x = margin + (usableW - w) / 2;
        const y = margin;
        pdf.addImage(imgData, 'JPEG', x, y, w, h);
      }
      pdf.save('Canon_Megatank_Bilinirlik_Kampanyasi.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const handleExportMegatankRevPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const el = mtRevSlideRef.current;
      if (!el) return;
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const canvas = await html2canvas(el, {
        scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
        scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
      const w = canvas.width * scale;
      const h = canvas.height * scale;
      const x = margin + (usableW - w) / 2;
      const y = margin;
      pdf.addImage(imgData, 'JPEG', x, y, w, h);
      pdf.save('Canon_Megatank_Revize_Plan_Rival.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  // Bir element içindeki tüm <img>'leri (Meta CDN cross-origin) backend proxy'siyle base64'e
  // çevirip src'lerini geçici olarak data-URL yapar; html2canvas'in yakalayabilmesi için.
  // Orijinal src'leri döndürür (export sonrası geri yüklemek için).
  const inlineImagesAsDataUrls = useCallback(async (root) => {
    if (!root) return [];
    const imgs = Array.from(root.querySelectorAll('img')).filter((im) => /^https?:/i.test(im.src));
    const originals = imgs.map((im) => ({ im, src: im.src }));
    const uniqueUrls = [...new Set(imgs.map((im) => im.src))];
    const map = {};
    for (let i = 0; i < uniqueUrls.length; i += 10) {
      const batch = uniqueUrls.slice(i, i + 10);
      try {
        const res = await fetch(`${API_BASE}/api/ai/image-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ urls: batch }),
        });
        const data = await res.json();
        for (const r of (data.results || [])) {
          if (r.success && r.base64) map[r.url] = `data:${r.contentType || 'image/jpeg'};base64,${r.base64}`;
        }
      } catch (_) { /* yoksay — bu görsel eski src'de kalır */ }
    }
    for (const { im, src } of originals) {
      if (map[src]) im.src = map[src];
    }
    // data-URL görsellerinin decode olmasını bekle (html2canvas'ten önce)
    await Promise.all(originals.map(({ im }) => (im.complete ? Promise.resolve() : new Promise((r) => { im.onload = im.onerror = r; }))));
    return originals;
  }, []);

  const handleExportMtReportPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    let restore = [];
    try {
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      // Kreatif preview görsellerini (Meta CDN) base64'e çevir — yoksa PDF'te boş çıkar.
      restore = await inlineImagesAsDataUrls(mtMediaRef.current);

      let anyPage = false;
      const addElementPaged = async (el) => {
        if (!el) return;
        const canvas = await html2canvas(el, {
          scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        });
        const pxPerMm = canvas.width / usableW;        // yatayı sayfaya sığdır
        const pageHpx = usableH * pxPerMm;             // bir sayfaya sığan canvas piksel yüksekliği
        let offset = 0;
        while (offset < canvas.height) {
          const slicePx = Math.min(pageHpx, canvas.height - offset);
          const slice = document.createElement('canvas');
          slice.width = canvas.width;
          slice.height = Math.round(slicePx);
          const ctx = slice.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, slice.width, slice.height);
          ctx.drawImage(canvas, 0, offset, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
          const imgData = slice.toDataURL('image/jpeg', 0.95);
          if (anyPage) pdf.addPage();
          anyPage = true;
          pdf.addImage(imgData, 'JPEG', margin, margin, usableW, slicePx / pxPerMm);
          offset += slicePx;
        }
      };

      await addElementPaged(mtReportRef.current);   // KPI + konsept + kampanya tablosu
      await addElementPaged(mtMediaRef.current);    // kreatif preview'lar + metrikler
      pdf.save('Canon_Megatank_Gerceklesen_Rapor.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      // Orijinal (canlı CDN) src'leri geri yükle
      for (const { im, src } of restore) im.src = src;
      setExporting(false);
    }
  }, [exporting, inlineImagesAsDataUrls]);

  const handleExportIfsakPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const el = ifsakSlideRef.current;
      if (!el) return;
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const canvas = await html2canvas(el, {
        scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
        scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
      const w = canvas.width * scale;
      const h = canvas.height * scale;
      const x = margin + (usableW - w) / 2;
      const y = margin;
      pdf.addImage(imgData, 'JPEG', x, y, w, h);
      pdf.save('Canon_IFSAK_Atolyesi_Acil_Push.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const TOTAL = 160000;
  const AGENCY_FEE_RATE = 0.10;
  const AGENCY_FEE = Math.round(TOTAL * AGENCY_FEE_RATE);
  const BRUT = TOTAL - AGENCY_FEE; // 144,000 (stopaj dahil medya bütçesi)
  const STOPAJ_RATE = 0.15;
  const NET = Math.round(BRUT * (1 - STOPAJ_RATE)); // 122,400
  const STOPAJ = BRUT - NET;

  const awarenessB = 47000;
  const engagementB = 42000;
  const reachB = NET - awarenessB - engagementB; // 33,400

  const campaigns = [
    {
      type: 'Awareness',
      objective: 'Brand Awareness & Video Views',
      desc: 'Kampanya bilinirliğini artırmak ve "Get Ready" mesajını geniş kitlelere ulaştırmak',
      budget: awarenessB,
      placements: 'FB Feed, IG Feed, IG Stories, IG Reels',
      targeting: '18-54, Fotoğrafçılık / Teknoloji / Kamera ilgi alanları, Canon sayfa etkileşimleri LAL',
      creative: 'Hafta 1-2: Statik görsel (kampanya duyurusu + ürün) + carousel (1-16 Nisan) · Hafta 3-4: 15s video (ürün demo) + Reels (17-30 Nisan)',
      cpm: 37.5,
      impressions: Math.round(awarenessB / 37.5 * 1000),
      reach: 700000,
      frequency: '1.7',
      clicks: 4500,
      cpc: (awarenessB / 4500).toFixed(2),
      videoViews: 180000,
      cpv: (awarenessB * 0.4 / 180000).toFixed(3),
      color: { border: '1px solid #bfdbfe', bg: '#eff6ff', text: '#1e40af', badge: '#2563eb' },
    },
    {
      type: 'Engagement',
      objective: 'Post Engagement & Video Views',
      desc: 'İçerikle etkileşimi artırmak, video izletmek ve kampanya sayfasına trafik çekmek',
      budget: engagementB,
      placements: 'FB Feed, IG Feed, IG Stories, IG Reels, IG Explore',
      targeting: 'Canon takipçileri, Fotoğraf ekipmanı ilgisi, Web sitesi ziyaretçileri (retargeting), Video izleyicileri',
      creative: 'Hafta 1-2: Carousel (öne çıkan ürünler + fiyat) + statik Story (1-16 Nisan) · Hafta 3-4: Reels video (kullanıcı deneyimi) + Story quiz (17-30 Nisan)',
      cpm: 50,
      impressions: Math.round(engagementB / 50 * 1000),
      reach: 450000,
      frequency: '1.8',
      clicks: 5700,
      cpc: (engagementB / 5700).toFixed(2),
      engagements: 20000,
      cpe: (engagementB / 20000).toFixed(2),
      color: { border: '1px solid #fde68a', bg: '#fffbeb', text: '#92400e', badge: '#d97706' },
    },
    {
      type: 'Reach',
      objective: 'Reach & Frequency',
      desc: 'Maksimum tekil kişiye ulaşarak kampanya farkındalığını yaymak (frekans kontrollü)',
      budget: reachB,
      placements: 'FB Feed, IG Feed, IG Stories, Audience Network',
      targeting: '18-54, Geniş hedefleme + Fotoğrafçılık / Teknoloji ilgisi, Türkiye geneli',
      creative: 'Hafta 1-2: Statik görsel (kampanya banneri) + Story swipe-up (1-16 Nisan) · Hafta 3-4: 6s bumper video + animated Story (17-30 Nisan)',
      cpm: 22.5,
      impressions: Math.round(reachB / 22.5 * 1000),
      reach: 900000,
      frequency: '1.7',
      clicks: 3400,
      cpc: (reachB / 3400).toFixed(2),
      color: { border: '1px solid #a7f3d0', bg: '#ecfdf5', text: '#065f46', badge: '#059669' },
    },
  ];

  const totalImpressions = campaigns.reduce((s, c) => s + c.impressions, 0);
  const totalClicks = campaigns.reduce((s, c) => s + c.clicks, 0);
  const totalReach = campaigns.reduce((s, c) => s + c.reach, 0);
  const avgCpc = (NET / totalClicks).toFixed(2);

  const fmtNum = (n) => Number(n).toLocaleString('tr-TR');
  const fmtCur = (n) => '₺' + Number(n).toLocaleString('tr-TR');

  // ─── WORKSHOP KAYIT KAMPANYASI DATA ───
  const WS_TOTAL = 75000;
  const WS_DESIGN = 28500;
  const WS_FEE_RATE = 0.10;
  const WS_FEE = Math.round(WS_TOTAL * WS_FEE_RATE);          // 7.500
  const WS_MEDIA_BRUT = WS_TOTAL - WS_DESIGN - WS_FEE;        // 39.000
  const WS_STOPAJ_RATE = 0.15;
  const WS_STOPAJ = Math.round(WS_MEDIA_BRUT * WS_STOPAJ_RATE); // 5.850
  const WS_NET = WS_MEDIA_BRUT - WS_STOPAJ;                     // 33.150

  const ws1Budget = Math.round(WS_NET * 0.50);                  // ~16.575
  const ws2Budget = Math.round(WS_NET * 0.25);                  // ~8.288
  const ws3Budget = WS_NET - ws1Budget - ws2Budget;              // ~8.287

  const workshopCampaigns = [
    {
      no: 1,
      location: 'Digione',
      date: '30 Nisan 2026',
      shortDate: '30 Nis',
      capacity: 60,
      budget: ws1Budget,
      adPeriod: '17 – 29 Nisan',
      cpc: 3.50,
      cpm: 45,
      clicks: Math.round(ws1Budget / 3.50),
      impressions: Math.round(ws1Budget / 45 * 1000),
      reach: Math.round(ws1Budget / 45 * 1000 * 0.55),
      targeting: 'Fotoğrafçılık / Kamera ilgi alanları, Canon sayfa etkileşimleri, İstanbul (Anadolu + Avrupa), 25-55 yaş, LAL kitleler',
      creative: 'Workshop tanıtım görseli + konuşmacı tanıtımı carousel + Story countdown + kayıt CTA',
      placements: 'FB Feed, IG Feed, IG Stories, IG Reels',
      color: { border: '1px solid #bfdbfe', bg: '#eff6ff', text: '#1e40af', badge: '#2563eb' },
    },
    {
      no: 2,
      location: 'Camlı Köşk',
      date: '14 Mayıs 2026',
      shortDate: '14 May',
      capacity: 30,
      budget: ws2Budget,
      adPeriod: '28 Nisan – 13 Mayıs',
      cpc: 3.80,
      cpm: 48,
      clicks: Math.round(ws2Budget / 3.80),
      impressions: Math.round(ws2Budget / 48 * 1000),
      reach: Math.round(ws2Budget / 48 * 1000 * 0.55),
      targeting: 'Fotoğrafçılık / Kamera ilgi alanları, Canon sayfa etkileşimleri, İstanbul (Avrupa yakası ağırlıklı), 25-55 yaş',
      creative: 'Mekan tanıtım görseli + Workshop 1 highlight carousel + Story countdown + kayıt CTA',
      placements: 'FB Feed, IG Feed, IG Stories, IG Reels',
      color: { border: '1px solid #fde68a', bg: '#fffbeb', text: '#92400e', badge: '#d97706' },
    },
    {
      no: 3,
      location: 'Kurtköy',
      date: '3 Haziran 2026',
      shortDate: '3 Haz',
      capacity: 30,
      budget: ws3Budget,
      adPeriod: '19 Mayıs – 2 Haziran',
      cpc: 3.80,
      cpm: 48,
      clicks: Math.round(ws3Budget / 3.80),
      impressions: Math.round(ws3Budget / 48 * 1000),
      reach: Math.round(ws3Budget / 48 * 1000 * 0.55),
      targeting: 'Fotoğrafçılık / Kamera ilgi alanları, Canon sayfa etkileşimleri, İstanbul (Anadolu yakası ağırlıklı + Kocaeli), 25-55 yaş',
      creative: 'Mekan tanıtım görseli + Workshop 1-2 highlight carousel + Story countdown + son çağrı CTA',
      placements: 'FB Feed, IG Feed, IG Stories, IG Reels',
      color: { border: '1px solid #a7f3d0', bg: '#ecfdf5', text: '#065f46', badge: '#059669' },
    },
  ];

  const wsTotalImpressions = workshopCampaigns.reduce((s, c) => s + c.impressions, 0);
  const wsTotalClicks = workshopCampaigns.reduce((s, c) => s + c.clicks, 0);
  const wsTotalReach = workshopCampaigns.reduce((s, c) => s + c.reach, 0);
  const wsTotalCapacity = workshopCampaigns.reduce((s, c) => s + c.capacity, 0);
  const wsAvgCpc = (WS_NET / wsTotalClicks).toFixed(2);

  // ─── MEGATANK BİLİNİRLİK KAMPANYASI DATA ───
  const MT_TOTAL = 250000;
  const MT_DESIGN = 38000;
  const MT_FEE_RATE = 0.10;
  const MT_FEE = Math.round(MT_TOTAL * MT_FEE_RATE);          // 25.000
  const MT_MEDIA_BRUT = MT_TOTAL - MT_DESIGN - MT_FEE;        // 187.000
  const MT_STOPAJ_RATE = 0.15;
  const MT_STOPAJ = Math.round(MT_MEDIA_BRUT * MT_STOPAJ_RATE); // 28.050
  const MT_NET = MT_MEDIA_BRUT - MT_STOPAJ;                     // 158.950

  const mtRivalBudget = Math.round(MT_NET * 0.50);              // ~79.475
  const mtVpBudget = MT_NET - mtRivalBudget;                    // ~79.475

  // CPM/CPV benchmarks (TR Meta Ads, awareness/video views)
  // NOT: Hedef kitle dar (Home: öğrenciler/çocuklu aileler, Office: küçük ofisler/home office) →
  // birim maliyetler benchmark'tan ~%80 yüksek alındı.
  const CPM_VIDEO = 58;   // 32 × 1.80
  const CPM_STATIC = 43;  // 24 × 1.80

  // 7 ad set: 4 Rival (home/office × video/static) + 3 Valuable Paper (home/office video + 1 mixed static)
  const megatankAdSets = [
    {
      id: 'mt-r-home-vid',
      concept: 'Rival',
      audience: 'Home',
      format: 'Video',
      assetId: 'de80c56f29ee81aae794b3903be9233571b54cef',
      assetLabel: 'Rival home — dikey video',
      budget: Math.round(mtRivalBudget * 0.30),
      cpm: CPM_VIDEO,
      targeting: 'Öğrenciler, çocuklu aileler, ev kullanıcıları, yazıcı/mürekkep ilgi alanları, 25-50 yaş, TR geneli',
      placements: 'IG Reels, IG Stories, FB Reels, FB Stories',
      phase: 'Faz 1 — Video',
      color: { border: '1px solid #fecaca', bg: '#fef2f2', text: '#991b1b', badge: '#dc2626' },
    },
    {
      id: 'mt-r-office-vid',
      concept: 'Rival',
      audience: 'Office',
      format: 'Video',
      assetId: '5a5ccaa971b6d51186e3a89470b7373e79f96f75',
      assetLabel: 'Rival office — dikey video',
      budget: Math.round(mtRivalBudget * 0.30),
      cpm: CPM_VIDEO,
      targeting: 'Küçük ofis sahipleri, home office çalışanlar, freelance, KOBİ karar verici, 28-55 yaş, TR geneli',
      placements: 'IG Reels, IG Stories, FB Reels, FB Stories',
      phase: 'Faz 1 — Video',
      color: { border: '1px solid #fecaca', bg: '#fef2f2', text: '#991b1b', badge: '#dc2626' },
    },
    {
      id: 'mt-r-home-stat',
      concept: 'Rival',
      audience: 'Home',
      format: 'Statik',
      assetId: 'b64386fc01f7421a13b57d8d43d7528635dea025',
      assetLabel: 'Rival home — statik post',
      budget: Math.round(mtRivalBudget * 0.20),
      cpm: CPM_STATIC,
      targeting: 'Öğrenciler, çocuklu aileler, ev kullanıcıları, yazıcı/mürekkep ilgi alanları, 25-50 yaş, TR geneli',
      placements: 'FB Feed, IG Feed',
      phase: 'Faz 2 — Statik',
      color: { border: '1px solid #fecaca', bg: '#fef2f2', text: '#991b1b', badge: '#dc2626' },
    },
    {
      id: 'mt-r-office-stat',
      concept: 'Rival',
      audience: 'Office',
      format: 'Statik',
      assetId: 'ddfcb70412688c845c22ebb79b298a7a0a9a4a43',
      assetLabel: 'Rival office — statik post',
      budget: mtRivalBudget - Math.round(mtRivalBudget * 0.30) * 2 - Math.round(mtRivalBudget * 0.20),
      cpm: CPM_STATIC,
      targeting: 'Küçük ofis sahipleri, home office çalışanlar, freelance, KOBİ karar verici, 28-55 yaş, TR geneli',
      placements: 'FB Feed, IG Feed',
      phase: 'Faz 2 — Statik',
      color: { border: '1px solid #fecaca', bg: '#fef2f2', text: '#991b1b', badge: '#dc2626' },
    },
    {
      id: 'mt-vp-home-vid',
      concept: 'Valuable Paper',
      audience: 'Home',
      format: 'Video',
      assetId: 'd3289865b0fdf0f2b825e1e6f89a8d95757c5e94',
      assetLabel: 'Valuable Paper home — video',
      budget: Math.round(mtVpBudget * 0.35),
      cpm: CPM_VIDEO,
      targeting: 'Öğrenciler, çocuklu aileler, ev kullanıcıları, baskı kalitesi ilgi alanları, 25-50 yaş, TR geneli',
      placements: 'IG Reels, IG Stories, FB Reels, FB Stories',
      phase: 'Faz 1 — Video',
      color: { border: '1px solid #bae6fd', bg: '#f0f9ff', text: '#075985', badge: '#0284c7' },
    },
    {
      id: 'mt-vp-office-vid',
      concept: 'Valuable Paper',
      audience: 'Office',
      format: 'Video',
      assetId: 'a5b922e563cdbcb6dabfa8a9d42ccb50e96c8289',
      assetLabel: 'Valuable Paper office — video',
      budget: Math.round(mtVpBudget * 0.35),
      cpm: CPM_VIDEO,
      targeting: 'Küçük ofis sahipleri, home office çalışanlar, freelance, KOBİ karar verici, 28-55 yaş, TR geneli',
      placements: 'IG Reels, IG Stories, FB Reels, FB Stories',
      phase: 'Faz 1 — Video',
      color: { border: '1px solid #bae6fd', bg: '#f0f9ff', text: '#075985', badge: '#0284c7' },
    },
    {
      id: 'mt-vp-static',
      concept: 'Valuable Paper',
      audience: 'Home + Office',
      format: 'Statik',
      assetId: '936d366c1aec6cdf9a7ea6bdb3c5fbb18d7eb675',
      assetLabel: 'Valuable Paper — statik post',
      budget: mtVpBudget - Math.round(mtVpBudget * 0.35) * 2,
      cpm: CPM_STATIC,
      targeting: 'Home + Office birleşik kitle, baskı kalitesi / dayanıklı belge ilgi alanları, 25-55 yaş, TR geneli',
      placements: 'FB Feed, IG Feed',
      phase: 'Faz 2 — Statik',
      color: { border: '1px solid #bae6fd', bg: '#f0f9ff', text: '#075985', badge: '#0284c7' },
    },
  ];

  // Compute derived metrics for each ad set
  megatankAdSets.forEach((a) => {
    a.impressions = Math.round(a.budget / a.cpm * 1000);
    a.reach = Math.round(a.impressions * 0.55);
    if (a.format === 'Video') {
      a.videoViews = Math.round(a.impressions * 0.32); // ~32% ThruPlay rate
      a.cpv = (a.budget / a.videoViews).toFixed(3);
    } else {
      a.clicks = Math.round(a.impressions * 0.008); // ~0.8% CTR for static awareness
      a.cpc = (a.budget / a.clicks).toFixed(2);
    }
  });

  const mtTotalImpressions = megatankAdSets.reduce((s, a) => s + a.impressions, 0);
  const mtTotalReach = megatankAdSets.reduce((s, a) => s + a.reach, 0);
  const mtTotalVideoViews = megatankAdSets.filter((a) => a.format === 'Video').reduce((s, a) => s + (a.videoViews || 0), 0);
  const mtTotalClicks = megatankAdSets.filter((a) => a.format === 'Statik').reduce((s, a) => s + (a.clicks || 0), 0);

  // ─── REVİZE PLAN — KALAN BÜTÇE 4 RIVAL İÇERİĞE ───
  // Faz 1'in Valuable Paper video ad set'leri yayınlandı; kalan bütçe yalnızca
  // Rival konseptinin 4 içeriğine (Home/Office × Video/Statik) aktarılır. VP statik iptal.
  const MT_VP_VIDEO_SPENT = megatankAdSets
    .filter((a) => a.concept === 'Valuable Paper' && a.format === 'Video')
    .reduce((s, a) => s + a.budget, 0);            // ~55.632 (yayınlandı)
  const MT_REMAINING = MT_NET - MT_VP_VIDEO_SPENT; // ~103.318

  // Kalan bütçenin 4 Rival içerik üzerindeki dağılımı (video ağırlıklı — awareness)
  const REV_WEIGHTS = {
    'mt-r-home-vid': 0.30,
    'mt-r-office-vid': 0.30,
    'mt-r-home-stat': 0.20,
    'mt-r-office-stat': 0.20, // son kalem kalan tutarı alır (yuvarlama farkı)
  };
  const revAdSets = megatankAdSets
    .filter((a) => a.concept === 'Rival')
    .map((a) => ({ ...a }));
  let revAllocated = 0;
  revAdSets.forEach((a, idx) => {
    if (idx < revAdSets.length - 1) {
      a.budget = Math.round(MT_REMAINING * REV_WEIGHTS[a.id]);
      revAllocated += a.budget;
    } else {
      a.budget = MT_REMAINING - revAllocated; // remainder
    }
    a.impressions = Math.round(a.budget / a.cpm * 1000);
    a.reach = Math.round(a.impressions * 0.55);
    if (a.format === 'Video') {
      a.videoViews = Math.round(a.impressions * 0.32);
      a.cpv = (a.budget / a.videoViews).toFixed(3);
      a.phase = 'Faz 1 — Video';
    } else {
      a.clicks = Math.round(a.impressions * 0.008);
      a.cpc = (a.budget / a.clicks).toFixed(2);
      a.phase = 'Faz 2 — Statik';
    }
  });
  const revTotalImpr = revAdSets.reduce((s, a) => s + a.impressions, 0);
  const revTotalReach = revAdSets.reduce((s, a) => s + a.reach, 0);
  const revTotalViews = revAdSets.filter((a) => a.format === 'Video').reduce((s, a) => s + (a.videoViews || 0), 0);
  const revTotalClicks = revAdSets.filter((a) => a.format === 'Statik').reduce((s, a) => s + (a.clicks || 0), 0);
  const revVideoBudget = revAdSets.filter((a) => a.format === 'Video').reduce((s, a) => s + a.budget, 0);
  const revStaticBudget = revAdSets.filter((a) => a.format === 'Statik').reduce((s, a) => s + a.budget, 0);

  // ─── İFSAK ATÖLYESİ — ACİL PUSH (20–24 Mayıs 2026) ───
  // Brief: Merve Jellybon (20.05.2026) — İFSAK atölyesine kayıt beklenenden düşük.
  // 18-25 yaş, fotoğrafçılık ilgi alanlı gençlere yönelik özel hedefleme.
  // AHK + her şey dahil 10K bütçe. Tasarım YOK (mevcut görseller kullanılacak).
  const IF_TOTAL = 10000;
  const IF_FEE_RATE = 0.10;
  const IF_FEE = Math.round(IF_TOTAL * IF_FEE_RATE);              // 1.000
  const IF_MEDIA_BRUT = IF_TOTAL - IF_FEE;                         // 9.000
  const IF_STOPAJ_RATE = 0.15;
  const IF_STOPAJ = Math.round(IF_MEDIA_BRUT * IF_STOPAJ_RATE);   // 1.350
  const IF_NET = IF_MEDIA_BRUT - IF_STOPAJ;                        // 7.650

  // Dar hedefleme (18-25 yaş + fotoğrafçılık ilgi alanı) + 5 günlük kısa pencere →
  // CPM benchmark üzeri alındı (~65 TL). Trafik objective, kayıt landing page'e yönlendirme.
  const IF_CPM = 65;
  const IF_CPC = 4.80;
  const IF_IMPRESSIONS = Math.round(IF_NET / IF_CPM * 1000);
  const IF_REACH = Math.round(IF_IMPRESSIONS * 0.55);
  const IF_CLICKS = Math.round(IF_NET / IF_CPC);
  const IF_DAYS = 5;
  const IF_DAILY = Math.round(IF_NET / IF_DAYS);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">

      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
        >
          {exporting ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          )}
          {exporting ? 'PDF Oluşturuluyor...' : 'PDF Olarak İndir'}
        </button>
      </div>

      {/* ═══ SLIDE 1: Overview ═══ */}
      <div ref={slide1Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200"></div>
            <div>
              <p className="text-xs font-bold text-gray-800">Nisan 2026 Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Awareness + Engagement + Reach</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya Bilgisi */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon Türkiye'nin 1-30 Nisan 2026 tarihlerinde geçerli olacak "Get Ready" kampanyası.
              Duyuru 1 Nisan'da sosyal medya hesaplarından yapılacak. Yönlendirme: canon.com.tr/offers/getready
            </p>
          </div>

          {/* Bütçe Özeti */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Toplam Bütçe</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(TOTAL)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Müşteri toplam bütçesi</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <span className="text-[10px] font-medium text-indigo-600 uppercase">Ajans Fee (%10)</span>
              <p className="text-xl font-bold text-indigo-700 mt-1">-{fmtCur(AGENCY_FEE)}</p>
              <p className="text-[10px] text-indigo-500 mt-0.5">{fmtCur(TOTAL)} × 0.10</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Medya Bütçesi (Stopaj Dahil)</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(BRUT)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{fmtCur(TOTAL)} − {fmtCur(AGENCY_FEE)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <span className="text-[10px] font-medium text-orange-600 uppercase">Stopaj (%15)</span>
              <p className="text-xl font-bold text-orange-700 mt-1">-{fmtCur(STOPAJ)}</p>
              <p className="text-[10px] text-orange-500 mt-0.5">{fmtCur(BRUT)} × 0.15</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <span className="text-[10px] font-medium text-emerald-600 uppercase">Net Medya Bütçesi</span>
              <p className="text-xl font-bold text-emerald-800 mt-1">{fmtCur(NET)}</p>
              <p className="text-[10px] text-emerald-500 mt-0.5">{fmtCur(BRUT)} × 0.85</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <span className="text-[10px] font-medium text-blue-600 uppercase">Tahmini Gösterim</span>
              <p className="text-xl font-bold text-blue-800 mt-1">{(totalImpressions / 1000000).toFixed(1)}M</p>
              <p className="text-[10px] text-blue-500 mt-0.5">Tüm kampanyalar toplamı</p>
            </div>
            <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
              <span className="text-[10px] font-medium text-purple-600 uppercase">Tahmini Erişim</span>
              <p className="text-xl font-bold text-purple-800 mt-1">~1.2M*</p>
              <p className="text-[10px] text-purple-500 mt-0.5">Overlap düşülmüş tekil kişi</p>
            </div>
          </div>

          {/* Kampanya Dağılım */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            {campaigns.map((c) => {
              const s = c.color;
              const pct = Math.round(c.budget / NET * 100);
              const metrics = [
                ['Gösterim', fmtNum(c.impressions)],
                ['Erişim', fmtNum(c.reach)],
                ['Tıklama', fmtNum(c.clicks)],
                ['CPM', fmtCur(c.cpm)],
                ['CPC', fmtCur(c.cpc)],
                ['Frekans', c.frequency + 'x'],
              ];
              if (c.videoViews) metrics.push(['Video İzleme', fmtNum(c.videoViews)]);
              if (c.engagements) metrics.push(['Etkileşim', fmtNum(c.engagements)]);
              return (
                <div key={c.type} style={{ border: s.border, backgroundColor: s.bg, borderRadius: 12, padding: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ display: 'inline-block', backgroundColor: s.badge, color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>{c.type}</span>
                    <span style={{ float: 'right', fontSize: 10, fontWeight: 600, color: '#9ca3af' }}>{pct}%</span>
                  </div>
                  <p style={{ fontSize: 20, fontWeight: 800, color: s.text, margin: 0 }}>{fmtCur(c.budget)}</p>
                  <p style={{ fontSize: 10, color: '#6b7280', marginTop: 4, lineHeight: 1.4 }}>{c.desc}</p>
                  <table style={{ width: '100%', marginTop: 12, borderCollapse: 'collapse' }}>
                    <tbody>
                      {metrics.map(([label, val]) => (
                        <tr key={label}>
                          <td style={{ fontSize: 10, color: '#6b7280', padding: '3px 0' }}>{label}</td>
                          <td style={{ fontSize: 10, fontWeight: 600, color: s.text, padding: '3px 0', textAlign: 'right' }}>{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* ═══ SLIDE 2: Campaign Plan ═══ */}
      <div ref={slide2Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Campaign Plan — Canon "Get Ready" Nisan 2026</div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kampanya</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hedef</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yerleşim</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gösterim</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Erişim</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tıklama</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bütçe</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {campaigns.map((c) => (
                  <tr key={c.type}>
                    <td className="px-4 py-3 text-sm font-medium">
                      <img src="/6033716.png" alt="Meta" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
                      {c.type}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.objective}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{c.placements}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtNum(c.impressions)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtNum(c.reach)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtNum(c.clicks)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(c.cpc)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(c.cpm)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(c.budget)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="px-4 py-3 text-sm" colSpan={3}>Grand Total (Meta Ads — FB + IG)</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtNum(totalImpressions)}</td>
                  <td className="px-4 py-3 text-sm text-right">~1.2M*</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtNum(totalClicks)}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(avgCpc)}</td>
                  <td className="px-4 py-3 text-sm text-right">—</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(NET)}</td>
                </tr>
              </tbody>
            </table>
            <div className="px-4 py-2 bg-gray-50 border-t">
              <p className="text-[10px] text-gray-400">* Toplam erişim, kampanyalar arası kitle çakışması (overlap) düşülerek hesaplanmıştır.</p>
            </div>
          </div>

      </div>

      {/* ═══ SLIDE 3: Hedefleme & Haftalık ═══ */}
      <div ref={slide3Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Hedefleme, Yerleşim & Kreatif Detayları</div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Kampanya</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Hedef Kitle</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Yerleşimler</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Kreatif Önerisi</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {campaigns.map((c) => (
                    <tr key={c.type} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{c.type}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{c.targeting}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{c.placements}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{c.creative}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>

          {/* Haftalık Akış */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Haftalık Harcama & Akış Planı</div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hafta</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kreatif Format</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Odak</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Awareness</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Haftalık Toplam</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {[
                    { week: 'Hafta 1', date: '1-7 Nisan', format: 'STATİK', focus: 'Lansman — Statik görsel + carousel ile kampanya duyurusu', aw: 15000, en: 9000, re: 12000 },
                    { week: 'Hafta 2', date: '8-16 Nisan', format: 'STATİK', focus: 'Statik devam — Ürün odaklı carousel + story görselleri (uzatıldı)', aw: 16000, en: 14000, re: 10400 },
                    { week: 'Hafta 3', date: '17-23 Nisan', format: 'VİDEO', focus: 'Video geçişi — 15s ürün video + Reels içerik', aw: 9000, en: 12000, re: 6000 },
                    { week: 'Hafta 4', date: '24-30 Nisan', format: 'VİDEO', focus: 'Final video push — 6s bumper + animated story + son çağrı', aw: 7000, en: 7000, re: 5000 },
                  ].map((w, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{w.week}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{w.date}</td>
                      <td className="px-4 py-3 text-xs">
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: w.format === 'STATİK' ? '#dbeafe' : '#fce7f3', color: w.format === 'STATİK' ? '#1e40af' : '#9d174d' }}>{w.format}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{w.focus}</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-700 font-medium">{fmtCur(w.aw)}</td>
                      <td className="px-4 py-3 text-sm text-right text-amber-700 font-medium">{fmtCur(w.en)}</td>
                      <td className="px-4 py-3 text-sm text-right text-emerald-700 font-medium">{fmtCur(w.re)}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-800">{fmtCur(w.aw + w.en + w.re)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm" colSpan={4}>Toplam</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(awarenessB)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(engagementB)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(reachB)}</td>
                    <td className="px-4 py-3 text-sm text-right">{fmtCur(NET)}</td>
                  </tr>
                </tbody>
              </table>
          </div>

          {/* Disclaimer */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-800">Tahmini Performans Projeksiyonları</p>
            <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
              Toplam bütçe: {fmtCur(TOTAL)} — Ajans fee (%10): {fmtCur(AGENCY_FEE)} — Medya bütçesi (brüt): {fmtCur(BRUT)} — Stopaj (%15): {fmtCur(STOPAJ)} — Net medya harcaması: {fmtCur(NET)}.
              Rakamlar Türkiye Meta Ads benchmark'larına dayalı tahmini projeksiyonlardır. Gerçek sonuçlar; açık artırma dinamikleri, hedef kitle davranışı, kreatif performansı ve pazar koşullarına bağlı olarak değişiklik gösterebilir.
            </p>
          </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ GET READY — GERÇEKLEŞEN (HAYAL ET) ════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <RealizedSection
        sectionTitle="GET READY — ACTUAL PERFORMANCE (Hayal Et Campaign)"
        sectionSubtitle="Canon Meta ads actual data — compared against plan"
        matchLabel="HayalEt"
        campaignFilter={(name) => name.includes('hayal') && !name.includes('atölye') && !name.includes('atolye')}
        defaultFrom="2026-04-01"
        defaultTo="2026-04-30"
        plannedImpressions={totalImpressions}
        plannedReach={totalReach}
        plannedClicks={totalClicks}
        plannedSpend={NET}
        pdfFilename="Canon_Get_Ready_Actual_Performance.pdf"
      />

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ WORKSHOP KAYIT KAMPANYASI ═══════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-red-200">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleExportWorkshopPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {exporting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
            {exporting ? 'PDF Oluşturuluyor...' : 'Workshop Planı PDF İndir'}
          </button>
        </div>
      </div>

      {/* ═══ WS SLIDE 1: Workshop Overview ═══ */}
      <div ref={wsSlide1Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200"></div>
            <div>
              <p className="text-xs font-bold text-gray-800">Workshop Kayıt Kampanyası — Trafik Reklamları</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — 3 Workshop, 120 Katılımcı Hedefi</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya Bilgisi */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Workshop Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon Türkiye'nin 3 farklı lokasyonda düzenleyeceği fotoğrafçılık workshop'ları için kayıt odaklı trafik kampanyası.
              Hedef: Workshop 1 (Digione) 60 kayıt, Workshop 2 (Camlı Köşk) ve Workshop 3 (Kurtköy) 30'ar kayıt — toplam 120 katılımcı.
            </p>
          </div>

          {/* Bütçe Özeti */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Toplam Bütçe</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(WS_TOTAL)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Tasarım + Hizmet + Reklam + Stopaj</p>
            </div>
            <div className="rounded-xl border border-pink-100 bg-pink-50 p-4">
              <span className="text-[10px] font-medium text-pink-600 uppercase">Tasarım</span>
              <p className="text-xl font-bold text-pink-700 mt-1">{fmtCur(WS_DESIGN)}</p>
              <p className="text-[10px] text-pink-500 mt-0.5">Kreatif üretim bedeli</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <span className="text-[10px] font-medium text-indigo-600 uppercase">Hizmet Bedeli (%10)</span>
              <p className="text-xl font-bold text-indigo-700 mt-1">{fmtCur(WS_FEE)}</p>
              <p className="text-[10px] text-indigo-500 mt-0.5">{fmtCur(WS_TOTAL)} x 0.10</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Medya Brüt</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(WS_MEDIA_BRUT)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{fmtCur(WS_TOTAL)} - {fmtCur(WS_DESIGN)} - {fmtCur(WS_FEE)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <span className="text-[10px] font-medium text-orange-600 uppercase">Stopaj (%15)</span>
              <p className="text-xl font-bold text-orange-700 mt-1">-{fmtCur(WS_STOPAJ)}</p>
              <p className="text-[10px] text-orange-500 mt-0.5">{fmtCur(WS_MEDIA_BRUT)} x 0.15</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <span className="text-[10px] font-medium text-emerald-600 uppercase">Net Reklam Bütçesi</span>
              <p className="text-xl font-bold text-emerald-800 mt-1">{fmtCur(WS_NET)}</p>
              <p className="text-[10px] text-emerald-500 mt-0.5">{fmtCur(WS_MEDIA_BRUT)} x 0.85</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <span className="text-[10px] font-medium text-blue-600 uppercase">Tahmini Tıklama</span>
              <p className="text-xl font-bold text-blue-800 mt-1">{fmtNum(wsTotalClicks)}</p>
              <p className="text-[10px] text-blue-500 mt-0.5">Ortalama CPC: {fmtCur(wsAvgCpc)}</p>
            </div>
            <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
              <span className="text-[10px] font-medium text-purple-600 uppercase">Hedef Kayıt</span>
              <p className="text-xl font-bold text-purple-800 mt-1">{fmtNum(wsTotalCapacity)} Kişi</p>
              <p className="text-[10px] text-purple-500 mt-0.5">60 + 30 + 30 katılımcı</p>
            </div>
          </div>

          {/* Workshop Dağılım */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            {workshopCampaigns.map((ws) => {
              const s = ws.color;
              const pct = Math.round(ws.budget / WS_NET * 100);
              const metrics = [
                ['Konum', ws.location],
                ['Etkinlik Tarihi', ws.date],
                ['Reklam Dönemi', ws.adPeriod],
                ['Hedef Kayıt', ws.capacity + ' kişi'],
                ['Gösterim', fmtNum(ws.impressions)],
                ['Erişim', fmtNum(ws.reach)],
                ['Tıklama', fmtNum(ws.clicks)],
                ['CPM', fmtCur(ws.cpm)],
                ['CPC', fmtCur(ws.cpc)],
              ];
              return (
                <div key={ws.no} style={{ border: s.border, backgroundColor: s.bg, borderRadius: 12, padding: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ display: 'inline-block', backgroundColor: s.badge, color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>Workshop {ws.no}</span>
                    <span style={{ float: 'right', fontSize: 10, fontWeight: 600, color: '#9ca3af' }}>{pct}%</span>
                  </div>
                  <p style={{ fontSize: 20, fontWeight: 800, color: s.text, margin: 0 }}>{fmtCur(ws.budget)}</p>
                  <p style={{ fontSize: 11, color: '#374151', marginTop: 4, fontWeight: 600 }}>{ws.location} — {ws.shortDate}</p>
                  <table style={{ width: '100%', marginTop: 10, borderCollapse: 'collapse' }}>
                    <tbody>
                      {metrics.map(([label, val]) => (
                        <tr key={label}>
                          <td style={{ fontSize: 10, color: '#6b7280', padding: '3px 0' }}>{label}</td>
                          <td style={{ fontSize: 10, fontWeight: 600, color: s.text, padding: '3px 0', textAlign: 'right' }}>{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ WS SLIDE 2: Campaign Table ═══ */}
      <div ref={wsSlide2Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Workshop Trafik Kampanyaları — Canon Kayıt Reklamları</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workshop</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Konum</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Hedef</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gösterim</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Erişim</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tıklama</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bütçe</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {workshopCampaigns.map((ws) => (
                <tr key={ws.no}>
                  <td className="px-4 py-3 text-sm font-medium">
                    <img src="/6033716.png" alt="Meta" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
                    Workshop {ws.no}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{ws.location}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{ws.shortDate}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{ws.capacity} kişi</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">{fmtNum(ws.impressions)}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtNum(ws.reach)}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtNum(ws.clicks)}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(ws.cpc)}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(ws.cpm)}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(ws.budget)}</td>
                </tr>
              ))}
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-4 py-3 text-sm" colSpan={3}>Grand Total (Meta Ads — Trafik)</td>
                <td className="px-4 py-3 text-sm text-center">{wsTotalCapacity} kişi</td>
                <td className="px-4 py-3 text-sm text-right">{fmtNum(wsTotalImpressions)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtNum(wsTotalReach)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtNum(wsTotalClicks)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(wsAvgCpc)}</td>
                <td className="px-4 py-3 text-sm text-right">—</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(WS_NET)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bütçe Dağılım Özeti */}
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Toplam Bütçe Dağılımı</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kalem</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Oran</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Tasarım & Kreatif Üretim</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(WS_DESIGN)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(WS_DESIGN / WS_TOTAL * 100)}%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Hizmet Bedeli (Ajans Fee)</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(WS_FEE)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(WS_FEE / WS_TOTAL * 100)}%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Stopaj (%15)</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(WS_STOPAJ)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(WS_STOPAJ / WS_TOTAL * 100)}%</td>
              </tr>
              <tr className="bg-emerald-50">
                <td className="px-4 py-3 text-sm font-bold text-emerald-800">Net Reklam Harcaması</td>
                <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{fmtCur(WS_NET)}</td>
                <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{Math.round(WS_NET / WS_TOTAL * 100)}%</td>
              </tr>
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-4 py-3 text-sm">Genel Toplam</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(WS_TOTAL)}</td>
                <td className="px-4 py-3 text-sm text-right">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ WS SLIDE 3: Hedefleme, Kreatif & Zaman Çizelgesi ═══ */}
      <div ref={wsSlide3Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Hedefleme, Yerleşim & Kreatif Detayları</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Workshop</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Hedef Kitle</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Yerleşimler</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Kreatif Önerisi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {workshopCampaigns.map((ws) => (
                <tr key={ws.no} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">
                    <span className="font-semibold">WS{ws.no}</span>
                    <span className="text-gray-500 ml-1">— {ws.location}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{ws.targeting}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{ws.placements}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{ws.creative}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Zaman Çizelgesi */}
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Reklam & Etkinlik Zaman Çizelgesi</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dönem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih Aralığı</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktivite</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">WS1 Digione</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">WS2 Camlı Köşk</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">WS3 Kurtköy</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {[
                { period: 'Faz 1', date: '17 – 29 Nisan', activity: 'WS1 ana push + WS2 teaser', ws1: Math.round(ws1Budget * 0.85), ws2: Math.round(ws2Budget * 0.15), ws3: 0 },
                { period: '', date: '30 Nisan', activity: 'Workshop 1 (Digione) — 60 kişi', ws1: 0, ws2: 0, ws3: 0, isEvent: 1 },
                { period: 'Faz 2', date: '28 Nisan – 13 Mayıs', activity: 'WS1 son push + WS2 ana push + WS3 teaser', ws1: Math.round(ws1Budget * 0.15), ws2: Math.round(ws2Budget * 0.70), ws3: Math.round(ws3Budget * 0.15) },
                { period: '', date: '14 Mayıs', activity: 'Workshop 2 (Camlı Köşk) — 30 kişi', ws1: 0, ws2: 0, ws3: 0, isEvent: 2 },
                { period: 'Faz 3', date: '19 Mayıs – 2 Haziran', activity: 'WS2 hatırlatma + WS3 ana push', ws1: 0, ws2: Math.round(ws2Budget * 0.15), ws3: Math.round(ws3Budget * 0.85) },
                { period: '', date: '3 Haziran', activity: 'Workshop 3 (Kurtköy) — 30 kişi', ws1: 0, ws2: 0, ws3: 0, isEvent: 3 },
              ].map((row, i) => (
                <tr key={i} className={row.isEvent ? 'bg-red-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">{row.period}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.date}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {row.isEvent ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                        <span className="font-bold text-red-700">{row.activity}</span>
                      </span>
                    ) : row.activity}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-blue-700 font-medium">{row.isEvent ? '—' : (row.ws1 > 0 ? fmtCur(row.ws1) : '—')}</td>
                  <td className="px-4 py-3 text-sm text-right text-amber-700 font-medium">{row.isEvent ? '—' : (row.ws2 > 0 ? fmtCur(row.ws2) : '—')}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-700 font-medium">{row.isEvent ? '—' : (row.ws3 > 0 ? fmtCur(row.ws3) : '—')}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-800">
                    {row.isEvent ? '—' : (row.ws1 + row.ws2 + row.ws3 > 0 ? fmtCur(row.ws1 + row.ws2 + row.ws3) : '—')}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-4 py-3 text-sm" colSpan={3}>Toplam Net Reklam Harcaması</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(ws1Budget)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(ws2Budget)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(ws3Budget)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(WS_NET)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Disclaimer */}
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-800">Tahmini Performans Projeksiyonları</p>
          <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
            Toplam bütçe: {fmtCur(WS_TOTAL)} — Tasarım: {fmtCur(WS_DESIGN)} — Hizmet bedeli (%10): {fmtCur(WS_FEE)} — Medya brüt: {fmtCur(WS_MEDIA_BRUT)} — Stopaj (%15): {fmtCur(WS_STOPAJ)} — Net reklam: {fmtCur(WS_NET)}.
            Kampanya hedefi: Trafik (Landing Page Views) optimizasyonlu kayıt reklamları. Workshop 1 (60 kişi) için bütçenin %50'si, Workshop 2 ve 3 (30'ar kişi) için %25'er ayrılmıştır.
            Rakamlar Türkiye Meta Ads benchmark'larına dayalı tahmini projeksiyonlardır.
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ WORKSHOP — GERÇEKLEŞEN (ÇEKİM ATÖLYESİ) ══════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <RealizedSection
        sectionTitle="WORKSHOP — ACTUAL PERFORMANCE (Çekim Atölyesi Campaign)"
        sectionSubtitle="Canon Meta ads actual data — compared against workshop plan"
        matchLabel="Çekim Atölyesi"
        campaignFilter={(name) => name.includes('atölye') || name.includes('atolye') || name.includes('cekim') || name.includes('çekim')}
        defaultFrom="2026-04-17"
        defaultTo="2026-06-02"
        plannedImpressions={wsTotalImpressions}
        plannedReach={wsTotalReach}
        plannedClicks={wsTotalClicks}
        plannedSpend={WS_NET}
        pdfFilename="Canon_Workshop_Actual_Performance.pdf"
      />

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ MEGATANK BİLİNİRLİK KAMPANYASI (Mayıs/Haziran 2026) ═════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-red-200">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleExportMegatankPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {exporting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
            {exporting ? 'PDF Oluşturuluyor...' : 'Megatank Planı PDF İndir'}
          </button>
        </div>
      </div>

      {/* ═══ MT SLIDE 1: Overview ═══ */}
      <div ref={mtSlide1Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200"></div>
            <div>
              <p className="text-xs font-bold text-gray-800">Megatank Bilinirlik Kampanyası — Mayıs/Haziran 2026</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Dark Campaign · Awareness · Video Views · 12 Haziran son</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya Bilgisi */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon Megatank serisinin yeni global kampanyalarının Türkiye'ye localize edilmesi ve sosyal medyada
              bilinirlik amacıyla yayınlanması. <strong>2 farklı konsept</strong> (Rival &amp; Valuable Paper) paralel olarak akacak.
              Akış: önce videolar aynı anda yayına alınır, ardından statikler aynı anda devreye girer.
              Kampanya tipi <strong>Dark</strong> (sayfa feed'inde görünmeyen) reklamlar; sadece Facebook + Instagram'da yayında.
              Son gün: <strong>12 Haziran 2026</strong>. Yönlendirme (Office): <em>Mürekkep Püskürtmeli Ofis Yazıcıları — canon.com.tr</em>.
              Home tarafında ise QR kod + landing page link kullanılacak.
            </p>
          </div>

          {/* Bütçe Özeti — Top Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Toplam Bütçe</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(MT_TOTAL)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Tasarım + Hizmet + Reklam + Stopaj</p>
            </div>
            <div className="rounded-xl border border-pink-100 bg-pink-50 p-4">
              <span className="text-[10px] font-medium text-pink-600 uppercase">Tasarım & Localization</span>
              <p className="text-xl font-bold text-pink-700 mt-1">{fmtCur(MT_DESIGN)}</p>
              <p className="text-[10px] text-pink-500 mt-0.5">2 konsept × dikey video + statik</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <span className="text-[10px] font-medium text-indigo-600 uppercase">Hizmet Bedeli (%10)</span>
              <p className="text-xl font-bold text-indigo-700 mt-1">{fmtCur(MT_FEE)}</p>
              <p className="text-[10px] text-indigo-500 mt-0.5">{fmtCur(MT_TOTAL)} × 0.10</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Medya Brüt (Stopaj Dahil)</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(MT_MEDIA_BRUT)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{fmtCur(MT_TOTAL)} − {fmtCur(MT_DESIGN)} − {fmtCur(MT_FEE)}</p>
            </div>
          </div>

          {/* Bütçe Özeti — Bottom Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <span className="text-[10px] font-medium text-orange-600 uppercase">Stopaj (%15)</span>
              <p className="text-xl font-bold text-orange-700 mt-1">-{fmtCur(MT_STOPAJ)}</p>
              <p className="text-[10px] text-orange-500 mt-0.5">{fmtCur(MT_MEDIA_BRUT)} × 0.15</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <span className="text-[10px] font-medium text-emerald-600 uppercase">Net Reklam Bütçesi</span>
              <p className="text-xl font-bold text-emerald-800 mt-1">{fmtCur(MT_NET)}</p>
              <p className="text-[10px] text-emerald-500 mt-0.5">{fmtCur(MT_MEDIA_BRUT)} × 0.85</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <span className="text-[10px] font-medium text-blue-600 uppercase">Tahmini Gösterim</span>
              <p className="text-xl font-bold text-blue-800 mt-1">{(mtTotalImpressions / 1000000).toFixed(1)}M</p>
              <p className="text-[10px] text-blue-500 mt-0.5">Tüm ad set'ler toplamı</p>
            </div>
            <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
              <span className="text-[10px] font-medium text-purple-600 uppercase">Tahmini Video İzleme</span>
              <p className="text-xl font-bold text-purple-800 mt-1">{fmtNum(mtTotalVideoViews)}</p>
              <p className="text-[10px] text-purple-500 mt-0.5">ThruPlay (~%32 oran)</p>
            </div>
          </div>

          {/* Konsept Dağılım — 2 büyük kart */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { name: 'Rival', budget: mtRivalBudget, count: 4, color: { border: '1px solid #fecaca', bg: '#fef2f2', text: '#991b1b', badge: '#dc2626' }, desc: 'Rakip yazıcı maliyetlerine karşı Megatank konumlandırması — Home + Office hedef kitlelerinde dikey video ve statik post.' },
              { name: 'Valuable Paper', budget: mtVpBudget, count: 3, color: { border: '1px solid #bae6fd', bg: '#f0f9ff', text: '#075985', badge: '#0284c7' }, desc: 'Baskı kalitesi ve dayanıklılık üzerine duygusal hikâye — Home + Office video, ortak statik post.' },
            ].map((cn) => {
              const sets = megatankAdSets.filter((a) => a.concept === cn.name);
              const imp = sets.reduce((s, a) => s + a.impressions, 0);
              const rch = sets.reduce((s, a) => s + a.reach, 0);
              const vv = sets.filter((a) => a.format === 'Video').reduce((s, a) => s + (a.videoViews || 0), 0);
              const pct = Math.round(cn.budget / MT_NET * 100);
              const s = cn.color;
              return (
                <div key={cn.name} style={{ border: s.border, backgroundColor: s.bg, borderRadius: 12, padding: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ display: 'inline-block', backgroundColor: s.badge, color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>{cn.name}</span>
                    <span style={{ float: 'right', fontSize: 10, fontWeight: 600, color: '#9ca3af' }}>{pct}%</span>
                  </div>
                  <p style={{ fontSize: 22, fontWeight: 800, color: s.text, margin: 0 }}>{fmtCur(cn.budget)}</p>
                  <p style={{ fontSize: 11, color: '#374151', marginTop: 4 }}>{cn.count} ad set · Home + Office</p>
                  <p style={{ fontSize: 10, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>{cn.desc}</p>
                  <table style={{ width: '100%', marginTop: 10, borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        ['Gösterim', fmtNum(imp)],
                        ['Erişim (tahmini)', fmtNum(rch)],
                        ['Video İzleme', fmtNum(vv)],
                      ].map(([label, val]) => (
                        <tr key={label}>
                          <td style={{ fontSize: 10, color: '#6b7280', padding: '3px 0' }}>{label}</td>
                          <td style={{ fontSize: 10, fontWeight: 600, color: s.text, padding: '3px 0', textAlign: 'right' }}>{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ MT SLIDE 2: Ad Set Tablosu + Bütçe Dağılımı ═══ */}
      <div ref={mtSlide2Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Ad Set Planı — Megatank Rival & Valuable Paper</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Konsept</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Hedef Kitle</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Format</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                <th className="px-3 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Gösterim</th>
                <th className="px-3 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Erişim</th>
                <th className="px-3 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Video / Tıklama</th>
                <th className="px-3 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">CPM</th>
                <th className="px-3 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Bütçe</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {megatankAdSets.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-3 text-sm font-medium">
                    <span style={{ display: 'inline-block', backgroundColor: a.color.badge, color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{a.concept}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-700">{a.audience}</td>
                  <td className="px-3 py-3 text-xs">
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: a.format === 'Statik' ? '#dbeafe' : '#fce7f3', color: a.format === 'Statik' ? '#1e40af' : '#9d174d' }}>{a.format}</span>
                  </td>
                  <td className="px-3 py-3 text-[10px] font-mono text-gray-500">{a.assetId.slice(0, 12)}…</td>
                  <td className="px-3 py-3 text-sm text-right">{fmtNum(a.impressions)}</td>
                  <td className="px-3 py-3 text-sm text-right">{fmtNum(a.reach)}</td>
                  <td className="px-3 py-3 text-sm text-right">
                    {a.format === 'Video' ? `${fmtNum(a.videoViews)} v` : `${fmtNum(a.clicks)} c`}
                  </td>
                  <td className="px-3 py-3 text-sm text-right">{fmtCur(a.cpm)}</td>
                  <td className="px-3 py-3 text-sm text-right font-semibold">{fmtCur(a.budget)}</td>
                </tr>
              ))}
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-3 py-3 text-sm" colSpan={4}>Grand Total (7 Ad Set · Meta Ads — FB + IG)</td>
                <td className="px-3 py-3 text-sm text-right">{fmtNum(mtTotalImpressions)}</td>
                <td className="px-3 py-3 text-sm text-right">{fmtNum(mtTotalReach)}</td>
                <td className="px-3 py-3 text-sm text-right">{fmtNum(mtTotalVideoViews)}v / {fmtNum(mtTotalClicks)}c</td>
                <td className="px-3 py-3 text-sm text-right">—</td>
                <td className="px-3 py-3 text-sm text-right">{fmtCur(MT_NET)}</td>
              </tr>
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t">
            <p className="text-[10px] text-gray-400">Asset ID'leri Public Folders/Other Folders/NSO/CEL DRBG/MegaTank altındadır. Video CPM benchmark: ₺{CPM_VIDEO} · Statik CPM: ₺{CPM_STATIC} · ThruPlay oranı: ~%32 · Statik CTR: ~%0.8.</p>
          </div>
        </div>

        {/* Bütçe Dağılım Özeti */}
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Toplam Bütçe Dağılımı</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kalem</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Oran</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Tasarım & Localization (2 konsept)</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(MT_DESIGN)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(MT_DESIGN / MT_TOTAL * 100)}%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Hizmet Bedeli (Ajans Fee · %10)</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(MT_FEE)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(MT_FEE / MT_TOTAL * 100)}%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-800">Stopaj (%15)</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(MT_STOPAJ)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(MT_STOPAJ / MT_TOTAL * 100)}%</td>
              </tr>
              <tr className="bg-emerald-50">
                <td className="px-4 py-3 text-sm font-bold text-emerald-800">Net Reklam Harcaması</td>
                <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{fmtCur(MT_NET)}</td>
                <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{Math.round(MT_NET / MT_TOTAL * 100)}%</td>
              </tr>
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-4 py-3 text-sm">Genel Toplam</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(MT_TOTAL)}</td>
                <td className="px-4 py-3 text-sm text-right">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ MT SLIDE 3: Hedefleme & Akış Planı ═══ */}
      <div ref={mtSlide3Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Hedefleme, Yerleşim & Kreatif Detayları</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Ad Set</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Hedef Kitle</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Yerleşimler</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Asset / Kreatif</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Faz</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {megatankAdSets.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-xs">
                    <span style={{ display: 'inline-block', backgroundColor: a.color.badge, color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{a.concept}</span>
                    <span className="ml-1 text-gray-700 font-medium">{a.audience}</span>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-gray-600">{a.targeting}</td>
                  <td className="px-3 py-3 text-[11px] text-gray-600">{a.placements}</td>
                  <td className="px-3 py-3 text-[11px] text-gray-600">
                    <p className="font-medium text-gray-800">{a.assetLabel}</p>
                    <p className="font-mono text-[10px] text-gray-400 break-all">{a.assetId}</p>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-gray-700 whitespace-nowrap">{a.phase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Akış / Zaman Çizelgesi */}
        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Akış Planı — Önce Video, Sonra Statik (12 Haziran son)</div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faz</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih Aralığı</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktivite</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rival</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valuable Paper</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {(() => {
                const rivalVideo = megatankAdSets.filter((a) => a.concept === 'Rival' && a.format === 'Video').reduce((s, a) => s + a.budget, 0);
                const rivalStatic = megatankAdSets.filter((a) => a.concept === 'Rival' && a.format === 'Statik').reduce((s, a) => s + a.budget, 0);
                const vpVideo = megatankAdSets.filter((a) => a.concept === 'Valuable Paper' && a.format === 'Video').reduce((s, a) => s + a.budget, 0);
                const vpStatic = megatankAdSets.filter((a) => a.concept === 'Valuable Paper' && a.format === 'Statik').reduce((s, a) => s + a.budget, 0);
                const rows = [
                  { phase: 'Faz 1', date: '15 – 28 Mayıs', format: 'VİDEO', focus: 'Lansman — Rival home/office video + VP home/office video aynı anda yayında', rival: rivalVideo, vp: vpVideo },
                  { phase: 'Faz 2', date: '29 Mayıs – 12 Haziran', format: 'STATİK', focus: 'Statik geçişi — Rival home/office statik + VP ortak statik aynı anda yayında', rival: rivalStatic, vp: vpStatic },
                  { phase: '', date: '12 Haziran 2026', format: 'KAPANIŞ', focus: 'Kampanya son günü — tüm ad set\'ler durdurulur', rival: 0, vp: 0, isEvent: true },
                ];
                return rows.map((row, i) => (
                  <tr key={i} className={row.isEvent ? 'bg-red-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">{row.phase}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.date}</td>
                    <td className="px-4 py-3 text-xs">
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                        backgroundColor: row.format === 'STATİK' ? '#dbeafe' : row.format === 'KAPANIŞ' ? '#fee2e2' : '#fce7f3',
                        color: row.format === 'STATİK' ? '#1e40af' : row.format === 'KAPANIŞ' ? '#991b1b' : '#9d174d' }}>{row.format}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {row.isEvent ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                          <span className="font-bold text-red-700">{row.focus}</span>
                        </span>
                      ) : row.focus}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-700 font-medium">{row.rival > 0 ? fmtCur(row.rival) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right text-sky-700 font-medium">{row.vp > 0 ? fmtCur(row.vp) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-800">{(row.rival + row.vp) > 0 ? fmtCur(row.rival + row.vp) : '—'}</td>
                  </tr>
                ));
              })()}
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-4 py-3 text-sm" colSpan={4}>Toplam Net Reklam Harcaması</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(mtRivalBudget)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(mtVpBudget)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtCur(MT_NET)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Disclaimer */}
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-800">Tahmini Performans Projeksiyonları</p>
          <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
            Toplam bütçe: {fmtCur(MT_TOTAL)} — Tasarım: {fmtCur(MT_DESIGN)} — Hizmet bedeli (%10): {fmtCur(MT_FEE)} — Medya brüt: {fmtCur(MT_MEDIA_BRUT)} — Stopaj (%15): {fmtCur(MT_STOPAJ)} — Net reklam: {fmtCur(MT_NET)}.
            Kampanya tipi: <strong>Dark</strong> Awareness + Video Views. Bütçe iki konsept arasında %50 / %50 dağıtılmış; her konseptin içinde Home + Office hedef kitleleri için ayrı ad set'ler açılır.
            Akış: önce videolar paralel yayında (Faz 1), ardından statikler paralel yayında (Faz 2). Son gün <strong>12 Haziran 2026</strong>.
            Rakamlar Türkiye Meta Ads benchmark'larına dayalı tahmini projeksiyonlardır; gerçek sonuçlar açık artırma dinamikleri ve kreatif performansına bağlı olarak değişiklik gösterebilir.
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ MEGATANK REVİZE PLAN — KALAN BÜTÇE 4 RIVAL İÇERİĞE ═══════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-rose-200">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleExportMegatankRevPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {exporting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
            {exporting ? 'PDF Oluşturuluyor...' : 'Revize Plan PDF İndir'}
          </button>
        </div>

        <div ref={mtRevSlideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
          {/* Başlık */}
          <div className="flex items-start gap-3">
            <span style={{ display: 'inline-block', backgroundColor: '#e11d48', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>REVİZE</span>
            <div>
              <p className="text-sm font-bold text-gray-800">Megatank — Kalan Bütçe Revize Planı (Rival Konsepti)</p>
              <p className="text-xs text-gray-500 mt-0.5">Faz 1 Valuable Paper videoları yayınlandı · kalan bütçe yalnızca 4 Rival içeriğe aktarılıyor</p>
            </div>
          </div>

          {/* Bütçe akış özeti */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border p-4 bg-gray-50">
              <p className="text-[11px] text-gray-500">Net Reklam Bütçesi</p>
              <p className="text-lg font-bold text-gray-800">{fmtCur(MT_NET)}</p>
            </div>
            <div className="rounded-xl border p-4 bg-sky-50 border-sky-200">
              <p className="text-[11px] text-sky-700">Harcanan — VP Video (Faz 1) ✓</p>
              <p className="text-lg font-bold text-sky-800">− {fmtCur(MT_VP_VIDEO_SPENT)}</p>
            </div>
            <div className="rounded-xl border p-4 bg-rose-50 border-rose-200">
              <p className="text-[11px] text-rose-700">Kalan — 4 Rival İçeriğe</p>
              <p className="text-lg font-bold text-rose-800">{fmtCur(MT_REMAINING)}</p>
            </div>
          </div>

          {/* Bilgi kutusu */}
          <div className="p-4 rounded-xl bg-rose-50 border border-rose-200">
            <p className="text-[11px] text-rose-800 leading-relaxed">
              Bu revizyonda <strong>Valuable Paper statik</strong> ad set'i devre dışı bırakıldı; kalan tüm bütçe ({fmtCur(MT_REMAINING)})
              Rival konseptinin <strong>4 içeriğine</strong> (Home video, Office video, Home statik, Office statik) dağıtıldı.
              Dağılım video ağırlıklı: <strong>Video %60</strong> ({fmtCur(revVideoBudget)}) · <strong>Statik %40</strong> ({fmtCur(revStaticBudget)}).
            </p>
          </div>

          {/* 4 içerik bütçe kartı */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {revAdSets.map((a) => (
              <div key={a.id} className="rounded-xl border p-4" style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2' }}>
                <div className="flex items-center gap-1 mb-1">
                  <span style={{ display: 'inline-block', backgroundColor: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>Rival</span>
                  <span className="text-xs font-semibold text-gray-700">{a.audience}</span>
                </div>
                <p className="text-[10px] text-gray-500">{a.format} · CPM ₺{a.cpm}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmtCur(a.budget)}</p>
                <p className="text-[10px] text-gray-500 mt-1">
                  {a.format === 'Video'
                    ? `~${(a.videoViews / 1000).toFixed(0)}K izleme · CPV ₺${a.cpv}`
                    : `~${(a.clicks / 1000).toFixed(1)}K tıklama · CPC ₺${a.cpc}`}
                </p>
              </div>
            ))}
          </div>

          {/* Ad set tablosu */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Ad Set Planı — Kalan Bütçe / 4 Rival İçerik</div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Ad Set</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Format</th>
                  <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Bütçe</th>
                  <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Gösterim</th>
                  <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Erişim</th>
                  <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">İzleme / Tıklama</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Faz</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {revAdSets.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-xs">
                      <span style={{ display: 'inline-block', backgroundColor: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>Rival</span>
                      <span className="ml-1 text-gray-700 font-medium">{a.audience}</span>
                    </td>
                    <td className="px-3 py-3 text-[11px] text-gray-600">{a.format}</td>
                    <td className="px-3 py-3 text-xs text-right font-semibold text-gray-800">{fmtCur(a.budget)}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-600">{a.impressions.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-600">{a.reach.toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-600">
                      {a.format === 'Video' ? `${a.videoViews.toLocaleString('tr-TR')} izleme` : `${a.clicks.toLocaleString('tr-TR')} tıklama`}
                    </td>
                    <td className="px-3 py-3 text-[11px] text-gray-700 whitespace-nowrap">{a.phase}</td>
                  </tr>
                ))}
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="px-3 py-3 text-xs" colSpan={2}>Toplam (Kalan Bütçe)</td>
                  <td className="px-3 py-3 text-xs text-right">{fmtCur(MT_REMAINING)}</td>
                  <td className="px-3 py-3 text-xs text-right">{revTotalImpr.toLocaleString('tr-TR')}</td>
                  <td className="px-3 py-3 text-xs text-right">{revTotalReach.toLocaleString('tr-TR')}</td>
                  <td className="px-3 py-3 text-xs text-right">{revTotalViews.toLocaleString('tr-TR')} izl · {revTotalClicks.toLocaleString('tr-TR')} tık</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Akış planı — sıkışık takvim */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Akış Planı — Önce Video, Sonra Statik (12 Haziran son)</div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faz</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih Aralığı</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktivite</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bütçe</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">Faz 1</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">4 – 8 Haziran</td>
                  <td className="px-4 py-3 text-xs"><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: '#fce7f3', color: '#9d174d' }}>VİDEO</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">Rival Home + Office dikey videoları aynı anda yayında</td>
                  <td className="px-4 py-3 text-sm text-right text-red-700 font-medium">{fmtCur(revVideoBudget)}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">Faz 2</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">8 – 12 Haziran</td>
                  <td className="px-4 py-3 text-xs"><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: '#dbeafe', color: '#1e40af' }}>STATİK</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">Rival Home + Office statik postları aynı anda yayında</td>
                  <td className="px-4 py-3 text-sm text-right text-red-700 font-medium">{fmtCur(revStaticBudget)}</td>
                </tr>
                <tr className="bg-red-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800"></td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">12 Haziran 2026</td>
                  <td className="px-4 py-3 text-xs"><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b' }}>KAPANIŞ</span></td>
                  <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-red-500"></span><span className="font-bold text-red-700">Kampanya son günü — tüm ad set'ler durdurulur</span></span></td>
                  <td className="px-4 py-3 text-sm text-right">—</td>
                </tr>
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="px-4 py-3 text-sm" colSpan={4}>Toplam Kalan Bütçe</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(MT_REMAINING)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Dipnot */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-[11px] text-amber-700 leading-relaxed">
              Kalan bütçe {fmtCur(MT_REMAINING)} = Net {fmtCur(MT_NET)} − yayınlanan VP video {fmtCur(MT_VP_VIDEO_SPENT)}.
              Dağılım Rival 4 içerik üzerinde: Home video %30 · Office video %30 · Home statik %20 · Office statik %20.
              CPM benchmark: Video ₺{CPM_VIDEO} · Statik ₺{CPM_STATIC} · ThruPlay ~%32 · Statik CTR ~%0.8.
              Takvim 12 Haziran deadline'ına göre sıkıştırıldı (Faz 1 video 4–8 Haziran, Faz 2 statik 8–12 Haziran).
              Rakamlar TR Meta Ads tahmini projeksiyonlarıdır.
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ MEGATANK GERÇEKLEŞEN RAPOR — CANLI META VERİSİ ══════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-emerald-200">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600">Gerçekleşen veri aralığı</label>
            <select
              value={mtRange}
              onChange={(e) => setMtRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Kampanya ömrü (tümü)</option>
              <option value="last90">Son 90 gün</option>
              <option value="last30">Son 30 gün</option>
              <option value="lastMonth">Geçen ay</option>
            </select>
            <button
              onClick={loadMtReport}
              disabled={mtLoading}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              {mtLoading ? 'Çekiliyor…' : 'Yenile'}
            </button>
          </div>
          <button
            onClick={handleExportMtReportPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {exporting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
            {exporting ? 'PDF Oluşturuluyor...' : 'Gerçekleşen Rapor + Kreatifler PDF'}
          </button>
        </div>

        <div ref={mtReportRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
          {/* Başlık */}
          <div className="flex items-start gap-3">
            <span style={{ display: 'inline-block', backgroundColor: '#059669', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>GERÇEKLEŞEN</span>
            <div>
              <p className="text-sm font-bold text-gray-800">Megatank Bilinirlik Kampanyası — Gerçekleşen Rapor</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Meta Ads (FB + IG) · Kampanya isminden eşleştirilen <strong>canlı</strong> veriler
                {mtRep?.window ? ` · ${mtRep.window.since} → ${mtRep.window.until}` : ''}
                {mtRep?.accountId ? ` · ${mtRep.accountId}` : ''}
              </p>
            </div>
          </div>

          {mtErr && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{mtErr}</div>
          )}
          {mtLoading && !mtRep && (
            <div className="text-sm text-gray-500 py-6 text-center">Canlı veri çekiliyor…</div>
          )}

          {mtRep && (() => {
            const sumK = (arr, k) => arr.reduce((t, c) => t + Number(c[k] || 0), 0);
            const camps = (mtRep.campaigns || [])
              .filter((c) => mtIsMatch(c.campaign_name))
              .map((c) => ({ ...c, ...mtParse(c.campaign_name) }))
              .sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));

            if (!camps.length) {
              return <div className="text-sm text-gray-500 py-6 text-center">Bu aralıkta eşleşen Megatank kampanyası bulunamadı.</div>;
            }

            const withRatios = (o) => ({
              ...o,
              ctr: o.impressions > 0 ? (o.clicks / o.impressions) * 100 : 0,
              cpm: o.impressions > 0 ? (o.spend / o.impressions) * 1000 : 0,
              cpc: o.clicks > 0 ? o.spend / o.clicks : 0,
            });
            const act = withRatios({
              spend: sumK(camps, 'spend'), impressions: sumK(camps, 'impressions'),
              reach: sumK(camps, 'reach'), clicks: sumK(camps, 'clicks'),
            });
            const rivalCamps = camps.filter((c) => c.concept === 'Rival');
            const vpCamps = camps.filter((c) => c.concept === 'Valuable Paper');
            const otherCamps = camps.filter((c) => c.concept !== 'Rival' && c.concept !== 'Valuable Paper');

            const fmt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const KPIS = [
              { label: 'Toplam Harcama', val: fmtCur(Math.round(act.spend)), sub: `${camps.length} kampanya`, color: 'emerald' },
              { label: 'Gösterim', val: fmtNum(Math.round(act.impressions)), sub: `CPM ₺${fmt2(act.cpm)}`, color: 'sky' },
              { label: 'Erişim', val: fmtNum(Math.round(act.reach)), sub: `Sıklık ${fmt2(act.reach > 0 ? act.impressions / act.reach : 0)}`, color: 'violet' },
              { label: 'Tıklama', val: fmtNum(Math.round(act.clicks)), sub: `CTR %${fmt2(act.ctr)} · CPC ₺${fmt2(act.cpc)}`, color: 'amber' },
            ];
            const KPI_BG = { emerald: 'bg-emerald-50 border-emerald-200', sky: 'bg-sky-50 border-sky-200', violet: 'bg-violet-50 border-violet-200', amber: 'bg-amber-50 border-amber-200' };

            const CONCEPTS = [
              { name: 'Rival', camps: rivalCamps, badge: '#dc2626', bg: '#fef2f2', bd: '#fecaca' },
              { name: 'Valuable Paper', camps: vpCamps, badge: '#0284c7', bg: '#f0f9ff', bd: '#bae6fd' },
            ];

            return (
              <>
                {/* KPI kartları */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {KPIS.map((k) => (
                    <div key={k.label} className={`rounded-xl border p-4 ${KPI_BG[k.color]}`}>
                      <p className="text-[11px] text-gray-500">{k.label}</p>
                      <p className="text-lg font-bold text-gray-900 mt-0.5">{k.val}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{k.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Genel Değerlendirme — canlı veriden yorum */}
                {(() => {
                  const notes = mtBuildInsights(camps);
                  if (!notes.length) return null;
                  return (
                    <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-5">
                      <div className="flex items-center gap-2 mb-2.5">
                        <span style={{ display: 'inline-block', width: 3, height: 16, borderRadius: 2, backgroundColor: '#059669' }} />
                        <p className="text-sm font-bold text-gray-800">Genel Değerlendirme</p>
                      </div>
                      <div className="space-y-2.5">
                        {notes.map((p, i) => (
                          <p key={i} className="text-[12.5px] leading-relaxed text-gray-700">{p}</p>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Konsept kırılımı */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CONCEPTS.map((cn) => {
                    const cSpend = sumK(cn.camps, 'spend');
                    const cImpr = sumK(cn.camps, 'impressions');
                    return (
                      <div key={cn.name} className="rounded-xl border p-4" style={{ borderColor: cn.bd, backgroundColor: cn.bg }}>
                        <div className="flex items-center gap-2 mb-1">
                          <span style={{ display: 'inline-block', backgroundColor: cn.badge, color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{cn.name}</span>
                          <span className="text-[11px] text-gray-500">{cn.camps.length} kampanya</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900 mt-1">{fmtCur(Math.round(cSpend))}</p>
                        <p className="text-[10px] text-gray-500 mt-1">{fmtNum(Math.round(cImpr))} gösterim</p>
                      </div>
                    );
                  })}
                </div>

                {/* Kampanya detayı */}
                <div className="rounded-xl border overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Kampanya Detayı — Gerçekleşen</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Kampanya</th>
                          <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Konsept</th>
                          <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Format</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Harcama</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Gösterim</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Erişim</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Tıklama</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                          <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">CPM</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {camps.map((c) => {
                          const d = withRatios({
                            spend: Number(c.spend || 0), impressions: Number(c.impressions || 0),
                            reach: Number(c.reach || 0), clicks: Number(c.clicks || 0),
                          });
                          return (
                            <tr key={c.campaign_id || c.campaign_name} className="hover:bg-gray-50">
                              <td className="px-3 py-2.5 text-[11px] text-gray-700 max-w-[260px] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                              <td className="px-3 py-2.5 text-[11px]">
                                <span style={{ display: 'inline-block', backgroundColor: c.concept === 'Rival' ? '#dc2626' : (c.concept === 'Valuable Paper' ? '#0284c7' : '#6b7280'), color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{c.concept}</span>
                                {c.audience !== 'Karma' && <span className="ml-1 text-gray-500">{c.audience}</span>}
                              </td>
                              <td className="px-3 py-2.5 text-[11px] text-gray-600">{c.format}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums font-semibold text-gray-800">{fmtCur(Math.round(d.spend))}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.impressions)}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.reach)}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.clicks)}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">%{fmt2(d.ctr)}</td>
                              <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">₺{fmt2(d.cpm)}</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-gray-800 text-white font-bold">
                          <td className="px-3 py-2.5 text-xs" colSpan={3}>Toplam ({camps.length} kampanya)</td>
                          <td className="px-3 py-2.5 text-xs text-right">{fmtCur(Math.round(act.spend))}</td>
                          <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.impressions))}</td>
                          <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.reach))}</td>
                          <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.clicks))}</td>
                          <td className="px-3 py-2.5 text-xs text-right">%{fmt2(act.ctr)}</td>
                          <td className="px-3 py-2.5 text-xs text-right">₺{fmt2(act.cpm)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {otherCamps.length > 0 && (
                  <p className="text-[11px] text-gray-400">
                    Not: {otherCamps.length} kampanya "Megatank" genel etiketiyle eşleşti (Rival/Valuable Paper ayrımı isimden çıkarılamadı).
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ MEGATANK MEDYA + HEDEF KİTLE — CANLI ════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-indigo-200">
        <div ref={mtMediaRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-5">
          <div className="flex items-start gap-3">
            <span style={{ display: 'inline-block', backgroundColor: '#4f46e5', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>MEDYA</span>
            <div>
              <p className="text-sm font-bold text-gray-800">Kampanya Bazında Kullanılan Medya</p>
              <p className="text-xs text-gray-500 mt-0.5">Her Megatank kampanyasının kreatifleri ve metrikleri — <strong>canlı</strong> Meta Graph API.</p>
            </div>
          </div>

          {mtMediaErr && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{mtMediaErr}</div>
          )}
          {mtMediaLoading && !mtMedia && (
            <div className="text-sm text-gray-500 py-6 text-center">Medya & hedef kitle çekiliyor…</div>
          )}
          {!mtMediaLoading && !mtMedia && !mtMediaErr && (
            <div className="text-sm text-gray-500 py-6 text-center">Önce yukarıdaki gerçekleşen rapor yüklenmeli.</div>
          )}

          {mtMedia && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(mtMedia.campaigns || []).map((c) => {
            const m = (mtRep?.campaigns || []).find((x) => x.campaign_id === c.campaign_id) || {};
            const fmt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const stats = [
              { label: 'Harcama', val: fmtCur(Math.round(Number(m.spend || 0))) },
              { label: 'Gösterim', val: fmtNum(Math.round(Number(m.impressions || 0))) },
              { label: 'Erişim', val: fmtNum(Math.round(Number(m.reach || 0))) },
              { label: 'Tıklama', val: fmtNum(Math.round(Number(m.clicks || 0))) },
              { label: 'CTR', val: `%${fmt2(m.ctr)}` },
              { label: 'CPM', val: `₺${fmt2(m.cpm)}` },
            ];
            return (
              <div key={c.campaign_id} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2.5 border-b border-gray-100">
                  <p className="text-[11px] font-mono text-gray-700 truncate" title={c.campaign_name}>{c.campaign_name || c.campaign_id}</p>
                </div>
                {c.error ? (
                  <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50">Çekilemedi: {c.error}</div>
                ) : (
                  <div className="p-4 space-y-3">
                    {/* Görüntü — büyük */}
                    {(c.creatives || []).length === 0 ? (
                      <p className="text-[11px] text-gray-400">Kreatif bulunamadı.</p>
                    ) : (
                      <div className="flex flex-wrap justify-center gap-2">
                        {(c.creatives || []).map((cr) => (
                          <div key={cr.ad_id} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100" title={cr.ad_name || cr.creative_name || ''}>
                            {cr.image_url || cr.thumbnail_url ? (
                              <img src={cr.image_url || cr.thumbnail_url} alt={cr.ad_name || 'kreatif'} className="h-72 w-auto object-contain" loading="lazy" />
                            ) : (
                              <div className="h-72 w-48 flex items-center justify-center text-[10px] text-gray-400">görsel yok</div>
                            )}
                            {cr.type === 'video' && (
                              <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] px-2 py-0.5 rounded-full">▶ video</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Görüntünün altında — kampanya metrikleri */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      {stats.map((s) => (
                        <div key={s.label} className="rounded-lg bg-gray-50 border border-gray-100 px-2 py-2 text-center">
                          <p className="text-[9px] text-gray-400 uppercase tracking-wide">{s.label}</p>
                          <p className="text-sm font-semibold text-gray-800 tabular-nums">{s.val}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
          )}

          <p className="text-[11px] text-gray-400">
            Kreatifler ve metrikler Meta Graph API'den canlı çekilir; görsel/video önizleme URL'leri Meta CDN'inde süreli olabilir (sayfa her açılışta yenilenir).
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ═══ İFSAK ATÖLYESİ — ACİL PUSH (20–24 Mayıs 2026) ═══════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      <div className="pt-8 border-t-4 border-red-200">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleExportIfsakPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {exporting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
            {exporting ? 'PDF Oluşturuluyor...' : 'İFSAK Acil Planı PDF İndir'}
          </button>
        </div>
      </div>

      <div ref={ifsakSlideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200"></div>
            <div>
              <p className="text-xs font-bold text-gray-800">İFSAK Atölyesi — Acil Kayıt Push Kampanyası</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Trafik · 20 – 24 Mayıs 2026 · 5 günlük dar hedef push</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Bütçe Özeti — Tasarım YOK */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Toplam Bütçe (AHK)</span>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmtCur(IF_TOTAL)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Hizmet + Reklam + Stopaj</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <span className="text-[10px] font-medium text-indigo-600 uppercase">Hizmet Bedeli (%10)</span>
              <p className="text-xl font-bold text-indigo-700 mt-1">{fmtCur(IF_FEE)}</p>
              <p className="text-[10px] text-indigo-500 mt-0.5">{fmtCur(IF_TOTAL)} x 0.10</p>
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <span className="text-[10px] font-medium text-orange-600 uppercase">Stopaj (%15)</span>
              <p className="text-xl font-bold text-orange-700 mt-1">-{fmtCur(IF_STOPAJ)}</p>
              <p className="text-[10px] text-orange-500 mt-0.5">{fmtCur(IF_MEDIA_BRUT)} x 0.15</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <span className="text-[10px] font-medium text-emerald-600 uppercase">Net Reklam Bütçesi</span>
              <p className="text-xl font-bold text-emerald-800 mt-1">{fmtCur(IF_NET)}</p>
              <p className="text-[10px] text-emerald-500 mt-0.5">~{fmtCur(IF_DAILY)}/gün × {IF_DAYS} gün</p>
            </div>
          </div>

          {/* Tahmini Performans */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <span className="text-[10px] font-medium text-blue-600 uppercase">Tahmini Gösterim</span>
              <p className="text-xl font-bold text-blue-800 mt-1">{fmtNum(IF_IMPRESSIONS)}</p>
              <p className="text-[10px] text-blue-500 mt-0.5">CPM: {fmtCur(IF_CPM)} (dar hedef)</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <span className="text-[10px] font-medium text-blue-600 uppercase">Tahmini Erişim</span>
              <p className="text-xl font-bold text-blue-800 mt-1">{fmtNum(IF_REACH)}</p>
              <p className="text-[10px] text-blue-500 mt-0.5">~%55 unique rate (yüksek freq.)</p>
            </div>
            <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
              <span className="text-[10px] font-medium text-purple-600 uppercase">Tahmini Tıklama (LP)</span>
              <p className="text-xl font-bold text-purple-800 mt-1">{fmtNum(IF_CLICKS)}</p>
              <p className="text-[10px] text-purple-500 mt-0.5">CPC: {fmtCur(IF_CPC)}</p>
            </div>
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <span className="text-[10px] font-medium text-red-600 uppercase">Yayın Süresi</span>
              <p className="text-xl font-bold text-red-700 mt-1">{IF_DAYS} Gün</p>
              <p className="text-[10px] text-red-500 mt-0.5">20 Mayıs → 24 Mayıs 2026</p>
            </div>
          </div>

          {/* Hedefleme & Yerleşim */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Hedefleme & Yerleşim</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-xs font-bold text-gray-700 mb-2">🎯 Hedef Kitle (Daraltılmış)</p>
                <ul className="text-[11px] text-gray-600 space-y-1.5 list-disc list-inside leading-relaxed">
                  <li><strong>Yaş:</strong> 18 – 25</li>
                  <li><strong>Lokasyon:</strong> İstanbul (Avrupa + Anadolu yakası) ağırlıklı, TR geneli geniş katman</li>
                  <li><strong>İlgi alanları:</strong> Fotoğrafçılık, DSLR / Aynasız kamera, Canon, Nikon, Sony, Adobe Lightroom, Sokak fotoğrafçılığı, Portre fotoğrafçılığı, İFSAK / Fotoğraf dernekleri</li>
                  <li><strong>Davranış:</strong> Üniversite öğrencileri, fotoğraf okulları, sanat & tasarım fakülteleri</li>
                  <li><strong>Custom audience:</strong> Canon TR sayfa etkileşimleri (180 gün), önceki workshop LP ziyaretçileri</li>
                  <li><strong>LAL:</strong> Workshop kayıt formu dönüşmüş kullanıcılar %1-3 (varsa)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-xs font-bold text-gray-700 mb-2">📱 Yerleşim & Kreatif Notu</p>
                <ul className="text-[11px] text-gray-600 space-y-1.5 list-disc list-inside leading-relaxed">
                  <li><strong>Yerleşimler:</strong> IG Reels, IG Stories, IG Feed, FB Reels (öncelik: Reels + Stories — Gen Z davranışı)</li>
                  <li><strong>Objective:</strong> Traffic — Landing Page Views (kayıt formu URL'i)</li>
                  <li><strong>Optimizasyon:</strong> Landing Page Views (Link Clicks değil)</li>
                  <li><strong>Bidding:</strong> Lowest Cost (manuel bid YOK — 5 günlük dar pencere için)</li>
                  <li><strong>Frequency cap:</strong> 3 / 7 gün (yüksek freq → mesaj penetrasyonu için)</li>
                  <li><strong>Kreatif:</strong> Mevcut İFSAK atölye görselleri + Story/Reel formatına 9:16 crop · son 2 günde "Son birkaç kontenjan" CTA varyantı</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bütçe Kırılımı Tablosu */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Bütçe Kırılımı (Ajans Bedeli + Reklam + Stopaj)</div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kalem</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Oran</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-800">Hizmet Bedeli (Ajans Fee — %10)</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(IF_FEE)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(IF_FEE / IF_TOTAL * 100)}%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-800">Stopaj (%15 — Meta üzerinden ödenir)</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">{fmtCur(IF_STOPAJ)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-500">{Math.round(IF_STOPAJ / IF_TOTAL * 100)}%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 text-sm font-bold text-emerald-800">Net Reklam Harcaması (Meta'ya giden)</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{fmtCur(IF_NET)}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">{Math.round(IF_NET / IF_TOTAL * 100)}%</td>
                </tr>
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="px-4 py-3 text-sm">Genel Toplam (AHK)</td>
                  <td className="px-4 py-3 text-sm text-right">{fmtCur(IF_TOTAL)}</td>
                  <td className="px-4 py-3 text-sm text-right">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Disclaimer */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-800">Notlar & Projeksiyon Varsayımları</p>
            <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
              Bu plan <strong>tasarım kalemi içermez</strong> — sadece Ajans Hizmet Bedeli + Net Reklam + Stopaj kalemlerinden oluşur (toplam {fmtCur(IF_TOTAL)} AHK). Hedef kitle dar (18-25 yaş + fotoğrafçılık ilgi alanları) ve yayın penceresi sadece 5 gün olduğundan birim maliyetler (CPM: {fmtCur(IF_CPM)}, CPC: {fmtCur(IF_CPC)}) TR Meta Ads benchmark'larının üzerinde alınmıştır.
              Tıklama → kayıt dönüşüm oranı landing page UX'ine ve kayıt formunun kısalığına doğrudan bağlıdır; ~%4-6 LP→kayıt CR ile <strong>~{fmtNum(Math.round(IF_CLICKS * 0.05))} ek kayıt</strong> hedeflenmektedir (orta senaryo).
              Reklamın <strong>20 Mayıs 2026</strong> içinde yayına alınması ve <strong>24 Mayıs 2026 sonunda</strong> durdurulması planlanmıştır.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// GET READY — GERÇEKLEŞEN section. Self-contained: fetches summary + adsets +
// creatives from /api/canon-report, renders 3 slides (genel KPI karşılaştırma,
// kampanya & adset breakdown, creative breakdown).
// ──────────────────────────────────────────────────────────────────────────

function RealizedSection({
  sectionTitle,
  sectionSubtitle,
  matchLabel,
  campaignFilter,
  defaultFrom,
  defaultTo,
  plannedImpressions,
  plannedReach,
  plannedClicks,
  plannedSpend,
  pdfFilename,
}) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const grSlide1Ref = useRef(null);
  const grSlide2Ref = useRef(null);
  const grSlide3Ref = useRef(null);
  const [exportingActual, setExportingActual] = useState(false);

  const handleExportActualPDF = useCallback(async () => {
    if (exportingActual) return;
    setExportingActual(true);
    try {
      const refs = [grSlide1Ref, grSlide2Ref, grSlide3Ref];
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      for (let i = 0; i < refs.length; i++) {
        const el = refs[i].current;
        if (!el) continue;
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(el, {
          scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const scale = Math.min(usableW / canvas.width, usableH / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        const x = margin + (usableW - w) / 2;
        const y = margin;
        pdf.addImage(imgData, 'JPEG', x, y, w, h);
      }
      pdf.save(pdfFilename || 'Canon_Actual_Performance.pdf');
    } catch (err) {
      console.error('Actual PDF export failed:', err);
    } finally {
      setExportingActual(false);
    }
  }, [exportingActual, pdfFilename]);

  const [summary, setSummary] = useState(null);
  const [adsets, setAdsets] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [matchedCampaignIds, setMatchedCampaignIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fmtNum = (n) => (n == null ? '–' : Number(n).toLocaleString('en-US'));
  const fmtCur = (n) => (n == null ? '–' : '₺' + Math.round(Number(n)).toLocaleString('en-US'));
  const fmtCurDec = (n) => (n == null ? '–' : '₺' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const fmtPct = (n, d = 2) => (n == null ? '–' : `${(Number(n) * 100).toFixed(d)}%`);

  const reload = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true); setError('');
    try {
      // 1) Load all Canon campaigns, then filter to only those whose name contains "HayalEt"
      const campRes = await fetch(`${API_BASE}/api/canon-report/campaigns`, { credentials: 'include' });
      const campJson = await campRes.json();
      if (!campRes.ok) throw new Error(campJson.error || 'campaigns failed');
      const ids = (campJson.campaigns || [])
        .filter((c) => campaignFilter((c.name || '').toLowerCase()))
        .map((c) => c.id);
      setMatchedCampaignIds(ids);

      if (ids.length === 0) {
        setSummary(null); setAdsets([]); setCreatives([]);
        setError(`No campaign matching "${matchLabel}" found for this brand.`);
        return;
      }

      const qs = new URLSearchParams({ from, to, campaignIds: ids.join(',') }).toString();
      const [sRes, aRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/api/canon-report/summary?${qs}`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/canon-report/adsets?${qs}`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/canon-report/creatives?${qs}`, { credentials: 'include' }),
      ]);
      const [s, a, c] = await Promise.all([sRes.json(), aRes.json(), cRes.json()]);
      if (!sRes.ok) throw new Error(s.error || 'summary failed');
      if (!aRes.ok) throw new Error(a.error || 'adsets failed');
      if (!cRes.ok) throw new Error(c.error || 'creatives failed');
      setSummary(s);
      setAdsets(a.adsets || []);
      setCreatives(c.creatives || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, campaignFilter, matchLabel]);

  useEffect(() => { reload(); }, [reload]);

  // Group adsets by campaign for slide 2
  const campaignGroups = adsets.reduce((acc, a) => {
    if (!acc[a.campaignId]) acc[a.campaignId] = { name: a.campaignName, objective: parseObjective(a.campaignName), adsets: [] };
    acc[a.campaignId].adsets.push(a);
    return acc;
  }, {});

  const k = summary?.kpi || {};
  const dPct = (real, plan) => (plan > 0 ? (real - plan) / plan : 0);
  const deltaImpr = dPct(k.paidImpressions || 0, plannedImpressions);
  const deltaReach = dPct(k.paidReach || 0, plannedReach);
  const deltaClicks = dPct(k.clicks ? k.clicks : ((k.ctr || 0) * (k.paidImpressions || 0)), plannedClicks);
  const deltaSpend = dPct(k.cost || 0, plannedSpend);

  return (
    <div className="pt-8 border-t-4 border-red-200">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-lg font-bold text-red-700">{sectionTitle}</p>
            <p className="text-xs text-red-600 mt-1">
              {sectionSubtitle}
              {matchedCampaignIds.length > 0 && ` · ${matchedCampaignIds.length} ${matchLabel} campaigns included`}
            </p>
          </div>
          <div className="flex items-end gap-2 text-xs flex-wrap">
            <label>Start<input type="date" value={from} onChange={e => setFrom(e.target.value)} className="ml-2 px-2 py-1 border rounded" /></label>
            <label>End<input type="date" value={to} onChange={e => setTo(e.target.value)} className="ml-2 px-2 py-1 border rounded" /></label>
            <button onClick={reload} disabled={loading} className="px-3 py-1 bg-red-600 text-white rounded font-semibold">
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button onClick={handleExportActualPDF} disabled={exportingActual || loading} className="px-3 py-1 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-400 text-white rounded font-semibold inline-flex items-center gap-2">
              {exportingActual ? (
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              )}
              {exportingActual ? 'Generating…' : 'Export PDF'}
            </button>
          </div>
        </div>
        {error && <div className="mt-2 text-red-700 text-xs">{error}</div>}
      </div>

      {/* GR SLIDE 1: Genel Plan vs Gerçekleşen */}
      <div ref={grSlide1Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-4 mb-6">
        <div className="flex items-center justify-between">
          <p className="text-base font-bold text-gray-800">Plan vs Actual — Overall KPI Comparison</p>
          <p className="text-[11px] text-gray-500">{from} → {to}</p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'IMPRESSIONS', plan: plannedImpressions, real: k.paidImpressions, delta: deltaImpr, fmt: fmtNum },
            { label: 'REACH',       plan: plannedReach,       real: k.paidReach,       delta: deltaReach, fmt: fmtNum },
            { label: 'CLICKS',      plan: plannedClicks,      real: Math.round((k.ctr || 0) * (k.paidImpressions || 0)), delta: deltaClicks, fmt: fmtNum },
            { label: 'SPEND',       plan: plannedSpend,       real: k.cost,            delta: deltaSpend, fmt: fmtCur },
          ].map((m) => (
            <div key={m.label} className="rounded-xl border border-gray-200 p-4">
              <div className="text-[10px] text-gray-500 font-semibold tracking-wider">{m.label}</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[9px] text-gray-400">PLAN</div>
                  <div className="text-sm font-semibold text-gray-700">{m.fmt(m.plan)}</div>
                </div>
                <div>
                  <div className="text-[9px] text-gray-400">ACTUAL</div>
                  <div className="text-sm font-bold text-gray-900">{m.real != null ? m.fmt(m.real) : '–'}</div>
                </div>
              </div>
              <div className={`mt-2 text-[11px] font-semibold ${m.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {m.real != null ? `${m.delta >= 0 ? '▲' : '▼'} ${fmtPct(Math.abs(m.delta), 1)}` : ''}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'ENGAGEMENT',   value: fmtNum(k.paidEngagement) },
            { label: 'ENGAGEMENT RATE', value: fmtPct(k.engagementRate) },
            { label: 'CTR',          value: fmtPct(k.ctr) },
            { label: 'CPM',          value: fmtCur(k.cpm) },
          ].map((m) => (
            <div key={m.label} className="rounded-xl bg-gray-50 border border-gray-200 p-3">
              <div className="text-[10px] text-gray-500 font-semibold tracking-wider">{m.label}</div>
              <div className="text-base font-bold text-gray-900 mt-1">{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* GR SLIDE 2: Campaign × Ad set (objective + targeting breakdown) */}
      <div ref={grSlide2Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-4 mb-6">
        <p className="text-base font-bold text-gray-800">Actual Performance by Campaign & Targeting</p>
        <p className="text-[11px] text-gray-500">Objective parsed from campaign name; targeting breakdown from ad set name.</p>
        <div className="space-y-4">
          {Object.entries(campaignGroups).map(([cid, g]) => {
            const totals = g.adsets.reduce((acc, a) => ({
              impr: acc.impr + a.impressions, reach: acc.reach + a.reach,
              clicks: acc.clicks + a.clicks, spend: acc.spend + a.spend,
              eng: acc.eng + a.engagement, views: acc.views + (a.views || 0),
            }), { impr: 0, reach: 0, clicks: 0, spend: 0, eng: 0, views: 0 });
            return (
              <div key={cid} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">{g.name}</div>
                    <div className="text-[10px] text-gray-500">Objective: <b>{g.objective}</b> · {g.adsets.length} ad set</div>
                  </div>
                  <div className="text-[11px] text-gray-600 flex gap-4 flex-wrap">
                    <span>Impr: <b>{fmtNum(totals.impr)}</b></span>
                    <span>Reach: <b>{fmtNum(totals.reach)}</b></span>
                    <span>Clicks: <b>{fmtNum(totals.clicks)}</b></span>
                    <span>Views: <b>{fmtNum(totals.views)}</b></span>
                    <span>Spend: <b>{fmtCur(totals.spend)}</b></span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-white">
                    <tr className="text-[10px] text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-2 text-left">Ad Set (Targeting)</th>
                      <th className="px-3 py-2 text-right">Impressions</th>
                      <th className="px-3 py-2 text-right">Reach</th>
                      <th className="px-3 py-2 text-right">Clicks</th>
                      <th className="px-3 py-2 text-right">CTR</th>
                      <th className="px-3 py-2 text-right">CPM</th>
                      <th className="px-3 py-2 text-right">CPC</th>
                      <th className="px-3 py-2 text-right">Views</th>
                      <th className="px-3 py-2 text-right">View Rate</th>
                      <th className="px-3 py-2 text-right">CPV</th>
                      <th className="px-3 py-2 text-right">Spend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {g.adsets.map((a) => (
                      <tr key={a.adsetId} className="text-sm">
                        <td className="px-4 py-2 text-gray-700">
                          <div className="font-medium">{displayAdsetName(a.adsetName)}</div>
                          <div className="text-[10px] text-gray-400">{targetingFromAdsetName(a.adsetName)}</div>
                        </td>
                        <td className="px-3 py-2 text-right">{fmtNum(a.impressions)}</td>
                        <td className="px-3 py-2 text-right">{fmtNum(a.reach)}</td>
                        <td className="px-3 py-2 text-right">{fmtNum(a.clicks)}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(a.ctr)}</td>
                        <td className="px-3 py-2 text-right">{fmtCur(a.cpm)}</td>
                        <td className="px-3 py-2 text-right">{a.clicks > 0 ? fmtCurDec(a.cpc) : '–'}</td>
                        <td className="px-3 py-2 text-right">{fmtNum(a.views)}</td>
                        <td className="px-3 py-2 text-right">{fmtPct(a.viewRate)}</td>
                        <td className="px-3 py-2 text-right">{a.views > 0 ? fmtCurDec(a.cpv) : '–'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtCur(a.spend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            );
          })}
          {Object.keys(campaignGroups).length === 0 && !loading && (
            <div className="text-sm text-gray-500 italic">No ad set data for this date range.</div>
          )}
        </div>
      </div>

      {/* GR SLIDE 3: By Creative */}
      <div ref={grSlide3Ref} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-4 mb-6">
        <p className="text-base font-bold text-gray-800">Actual Performance by Creative</p>
        <p className="text-[11px] text-gray-500">Top 12 creatives by impressions. Thumbnails are served from Meta's CDN — if they fail to load, only text is shown.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {creatives.slice(0, 12).map((c) => (
            <div key={c.creativeId} className="rounded-xl border border-gray-200 overflow-hidden flex flex-col">
              <div className="bg-gray-100 aspect-[4/5] flex items-center justify-center relative">
                <CreativeImage creativeId={c.creativeId} isVideo={c.isVideo} />
              </div>
              <div className="p-3 space-y-1">
                <div className="text-[11px] font-semibold text-gray-800 truncate" title={c.creativeName}>
                  {c.creativeName}
                </div>
                {c.title && <div className="text-[10px] text-gray-500 truncate">{c.title}</div>}
                <div className="grid grid-cols-2 gap-1 mt-2 text-[10px]">
                  <div><span className="text-gray-400">Impr:</span> <b>{fmtNum(c.impressions)}</b></div>
                  <div><span className="text-gray-400">Clk:</span> <b>{fmtNum(c.clicks)}</b></div>
                  <div><span className="text-gray-400">CTR:</span> <b>{fmtPct(c.ctr)}</b></div>
                  <div><span className="text-gray-400">CPM:</span> <b>{fmtCur(c.cpm)}</b></div>
                  <div><span className="text-gray-400">View:</span> <b>{fmtNum(c.views)}</b></div>
                  <div><span className="text-gray-400">View Rate:</span> <b>{fmtPct(c.viewRate)}</b></div>
                  <div><span className="text-gray-400">CPV:</span> <b>{c.views > 0 ? fmtCurDec(c.cpv) : '–'}</b></div>
                  <div><span className="text-gray-400">CPC:</span> <b>{c.clicks > 0 ? fmtCurDec(c.cpc) : '–'}</b></div>
                  <div className="col-span-2 pt-1 border-t border-gray-100"><span className="text-gray-400">Spend:</span> <b>{fmtCur(c.spend)}</b></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {creatives.length === 0 && !loading && (
          <div className="text-sm text-gray-500 italic">Bu tarih aralığında creative verisi yok.</div>
        )}
      </div>
    </div>
  );
}

// Live-fetched creative thumbnail. Reuses the same proven endpoint used by
// /ads-platforms/meta-ads/creative-performance — it asks Meta Graph for a
// thumbnail at the requested width/height (Meta supports thumbnail_width/
// thumbnail_height query params), then proxies the bytes back.
const CANON_BRAND_ID_CLIENT = '3987361f-4006-4819-bd83-f78b5a2155ec';

function CreativeImage({ creativeId, isVideo }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setBlobUrl(null);
    setFailed(false);

    const params = new URLSearchParams({
      creativeId,
      brandId: CANON_BRAND_ID_CLIENT,
      w: '512',
      h: '512',
    });
    fetch(`${API_BASE}/api/meta-ads/live/creative-thumbnail?${params.toString()}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((blob) => {
        if (cancelled) return;
        // 1x1 transparent fallback comes back when Meta has no thumbnail
        if (blob.size < 200) {
          setFailed(true);
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [creativeId]);

  if (failed) {
    return (
      <div className="text-[10px] text-gray-400 text-center px-2">
        {isVideo ? 'Video' : 'Static'} #{creativeId.slice(-6)}
        <div className="text-[9px] text-gray-300 mt-1">thumbnail unavailable</div>
      </div>
    );
  }
  if (!blobUrl) {
    return <div className="text-[10px] text-gray-300">…</div>;
  }
  return <img src={blobUrl} alt="" className="w-full h-full object-contain" />;
}

// Display rename for ad set names — generic "New Traffic / New Engagement Ad Set"
// placeholders both represent new-user prospecting audiences in Canon's account.
function displayAdsetName(name) {
  if (!name) return '';
  const n = name.trim();
  if (n === 'New Traffic Ad Set' || n === 'New Engagement Ad Set') return 'New User';
  return n;
}

// Objective: campaign name 2nd segment (JB_<Objective>_…)
function parseObjective(name) {
  if (!name) return '–';
  const parts = name.split('_');
  if (parts.length >= 2) return parts[1];
  return name;
}

// Targeting summary inferred from adset name patterns.
function targetingFromAdsetName(name) {
  if (!name) return '';
  const n = name.toLowerCase();
  if (n.includes('rm') || n.includes('remarket')) return 'Remarketing — site visitors';
  if (n.includes('lookalike') || n.includes('lal')) return 'Lookalike — similar audience';
  if (n.includes('newuser')) return 'New User — new user prospecting';
  if (n.includes('engagement')) return 'Engagement — engagement-focused broad';
  if (n.includes('traffic')) return 'Traffic — broad prospecting';
  return '';
}

